import { Modal } from '@/components/ui/Modal';
import { SignedFileLink } from '@/components/ui/SignedImage';
import { usePincodeAutofill } from '@/hooks/usePincodeAutofill';
// useMemo no longer needed
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useEffect, useState, Fragment } from 'react';
import {
  ArrowLeft, Loader2, MapPin, Phone, Mail, Package, Truck, User as UserIcon, X, Save, RefreshCw, Undo2,
  Calendar, Ticket, XCircle, Download, Printer, FileText, MessageSquare,
  CheckCircle2, ChevronDown, Pencil, Copy,
} from 'lucide-react';
import { API_BASE_URL, tokenStore } from '@/api/client';
import { api, downloadBlob } from '@/api/client';
import { useAuth } from '@/hooks/useAuth';
import { CopyText } from '@/components/ui/CopyText';
import { MoneyReceiptButton } from '@/components/order/MoneyReceiptButton';
import { CallButton } from '@/components/order/CallButton';
import { CallRecordingsButton } from '@/components/order/CallRecordingsButton';
import { InitiateReturnDialog } from './InitiateReturnDialog';
import { OrderRemarks } from '@/components/OrderRemarks';
import { OrderEditHistory } from '@/components/OrderEditHistory';
import { ChangeMerchantButton } from '@/components/order/ChangeMerchantButton';
import { ChangeWaybillButton } from '@/components/order/ChangeWaybillButton';
import { CancelDispatchPanel } from '@/components/order/CancelDispatchPanel';
import { BookAppointmentModal } from '@/features/appointments/AppointmentsPage';
import { RouteMapCard, RoutePoint } from '@/components/ui/RouteMap';
import { useGeocode } from '@/hooks/useGeocode';

/** Whole days between two dates (>= 0). Used for TAT (Booking Received → current status). */
/**
 * The merchant's order date, DD-MM-YYYY in IST.
 *
 * `toLocaleString('en-IN')` rendered it in the VIEWER'S timezone, and the column holds IST midnight
 * (10-09-2026 is stored as 2026-09-09T18:30:00Z), so a browser west of IST showed the previous day.
 * It also printed a meaningless "12:00:00 am" on a date-only value. Matches the export exactly.
 */
function orderDateIst(v?: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '—';
  const ist = new Date(d.getTime() + 5.5 * 3600 * 1000);
  const p2 = (n: number) => String(n).padStart(2, '0');
  return `${p2(ist.getUTCDate())}-${p2(ist.getUTCMonth() + 1)}-${ist.getUTCFullYear()}`;
}

function daysBetween(from?: string | null, to?: string | null): number | null {
  if (!from) return null;
  const a = new Date(from).getTime();
  const b = to ? new Date(to).getTime() : Date.now();
  if (isNaN(a) || isNaN(b)) return null;
  return Math.max(0, Math.round((b - a) / 86400000));
}

interface OrderDetail {
  id: string; orderNo: string; systemOrderNo: string; courierOrderRef?: string; orderType: 'B2C' | 'B2B';
  awbNo?: string; lrNo?: string; statusId: number;
  bookingDate: string; lastEventDate?: string; shipDate?: string; edd?: string;
  capturedWeight?: string;
  deliveryType: 'COD' | 'PREPAID';
  customerName: string; customerMobile: string; customerEmail?: string;
  customerAddress: string; customerCity: string; customerState: string; customerPincode: string;
  customerAltMobile?: string; customerLandmark?: string;
  pickupAddress: string; pickupPincode: string;
  pickupPersonName?: string; pickupPersonPhone?: string;
  invoiceValue: string; codValue: string; chargeableWeight?: string;
  products: any[]; dimensions?: any; pieces?: any[];
  ewayBillNo?: string; ewayBillNoResolved?: string; insuranceType?: string; freightCollection?: string;
  totalBoxes?: number;
  merchantId?: number; warehouseId?: number; courierId?: number; pickupType?: string;
  jobId?: string;
  userRemarks?: string; subOrders?: any[];
  merchant?: { id: number; companyName: string; brandName?: string };
  courier?: { id: number; name: string; code: string; brand?: string };
  /** Server-decided: is this shipment on Gaint's own (in-house) network? Drives the post-ship edit window. */
  isOwnCourier?: boolean;
  subAwbs?: Array<{ id: number; awbNo: string; isMaster: boolean; boxNo: number; totalBoxes: number; label?: string; weight?: string; dimensions?: any; oid?: string;
    lastScanAt?: string | null; scanCount?: number;
    scans?: Array<{ id: string; statusId: number; statusMessage?: string; eventDateTime: string; location?: string; scanCity?: string; scanState?: string; scanPincode?: string; receivedVia: string }>;
  }>;
  trackingEvents?: Array<{
    id: string; statusId: number; statusMessage?: string;
    eventDateTime: string; location?: string; scanCity?: string; scanState?: string; scanPincode?: string;
    receivedVia: string; receiverName?: string; receiverPhone?: string; podFile?: string;
    /** Who made a manual scan. Internal callers only — the API omits it for a merchant. */
    updatedBy?: string | null;
  }>;
}

const STATUS_LABEL: Record<number, string> = {
  1:'Booking Received', 2:'Pickup Scheduled', 3:'Picked', 4:'At Branch', 5:'In Transit',
  6:'Arrived at Dest', 7:'Out for Delivery', 8:'Delivered', 9:'Delivery Failed',
  10:'RTO In Transit', 11:'RTO At Branch', 12:'RTO Delivered', 13:'LOST', 14:'RTO LOST',
  15:'Forward Damage', 16:'RTO Damage', 17:'Held UP', 18:'RTO Pending', 19:'Partially Delivered',
  20:'Booking Pending', 21:'Booking Failed', 23:'Cancelled', 24:'Not Shipped',
  25:'Received at Noida Branch', 26:'Wrong Book', 27:'Shipment Left for Lucknow',
  28:'Received at Kolkata Branch', 29:'Received at Lucknow Branch',
  30:'Shipment Left for Next Facility Noida', 32:'Shipment Left for Next Facility', 33:'Auto Cancelled',
};
const STATUS_TONE: Record<number, string> = {
  1:'bg-blue-100 text-blue-700', 2:'bg-blue-100 text-blue-700',
  3:'bg-amber-100 text-amber-700', 4:'bg-amber-100 text-amber-700', 5:'bg-amber-100 text-amber-700',
  6:'bg-indigo-100 text-indigo-700', 7:'bg-indigo-100 text-indigo-700',
  8:'bg-emerald-100 text-emerald-700', 19:'bg-emerald-100 text-emerald-700',
  9:'bg-rose-100 text-rose-700', 13:'bg-rose-100 text-rose-700', 14:'bg-rose-100 text-rose-700',
  23:'bg-slate-200 text-slate-600', 24:'bg-slate-100 text-slate-500', 20:'bg-slate-100 text-slate-500',
};

// Delhivery's B2B sub-couriers are coded `light_weight_40` / `heavy_shipment_39` / `only_books_41` —
// no "delhivery" in the code at all — so a code-prefix test hid Try & Track and Download E-POD on every
// B2B Delhivery shipment. Match the BRAND too (the courier-client factory resolves clients the same way).
function isBrand(courier: { code?: string; brand?: string } | undefined, brand: string): boolean {
  const b = (courier?.brand ?? '').toLowerCase();
  const c = (courier?.code ?? '').toLowerCase();
  return b === brand || c.startsWith(brand) || c.includes(brand);
}

// Delivered (8) / Partially Delivered (19). NOT 12 — that is "RTO Delivered to Merchant".
const EPOD_STATUSES = [8, 19];

/**
 * Statuses where even an own-network order stops being editable — delivered (8, 19), RTO-delivered
 * (11, 12), cancelled (23, 33). Must stay identical to `EDIT_LOCKED_STATUSES` in the backend's
 * `common/order-edit-log.util.ts`; if they drift the button offers an edit the API then refuses.
 */
const EDIT_LOCKED_STATUSES = [8, 11, 12, 19, 23, 33, 34, 35, 36, 37, 38];

/**
 * Shared shape for every action button in the order-detail header.
 *
 * They used to each carry their own padding — `px-4 py-2` on most, `px-3 py-2` on Downloads,
 * `px-3 py-1.5` inside MoneyReceiptButton — so the row rendered at three different heights and the
 * buttons did not line up. A fixed `h-9` plus a single padding value is what keeps them level; only
 * the colours differ per button. `whitespace-nowrap` stops "Edit Shipped Order" wrapping to two
 * lines and dragging its neighbours out of alignment when the row gets tight.
 */
const ACTION_BTN = 'h-9 px-3.5 rounded-lg border text-sm font-medium inline-flex items-center justify-center gap-1.5 whitespace-nowrap disabled:opacity-50 transition-colors';

export function OrderDetailPage({ orderType }: { orderType: 'B2C' | 'B2B' }) {
  const { id = '' } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [editOpen, setEditOpen] = useState(false);
  const [apptOpen, setApptOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  useEffect(() => { if (searchParams.get('edit') === '1') setEditOpen(true); }, [searchParams]);
  const qc = useQueryClient();
  const isMerchantUser = useAuth((s) => s.user)?.kind === 'merchant';

  const { data, isLoading, error } = useQuery({
    queryKey: ['order', id],
    queryFn: async () => (await api.get<OrderDetail>(`/orders/${id}`)).data,
    enabled: !!id,
  });

  const bookMut = useMutation({
    mutationFn: async () => (await api.post(`/orders/${id}/book-waybill`)).data,
    onSuccess: (d: any) => { toast.success(`Waybill: ${d.generated}`); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Book failed'),
    // Refresh this order AND the orders list + tab counts (B2C/B2B) whether the (re)ship succeeded or
    // failed — a failed booking still flips the order to Booking-Failed and changes the counts.
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['order', id] });
      qc.invalidateQueries({ predicate: (q: any) => Array.isArray(q.queryKey) && (q.queryKey[0] === 'orders' || q.queryKey[0] === 'order-tab-counts') });
    },
  });
  const [cancelPwOpen, setCancelPwOpen] = useState(false);
  const [cancelPw, setCancelPw] = useState('');
  const [cancelRemark, setCancelRemark] = useState('');
  const [cancelBusy, setCancelBusy] = useState(false);
  const cancelMut = useMutation({
    mutationFn: async (password: string) => {
      const v = await api.post('/auth/verify-password', { password });
      if (!(v.data as any)?.ok) throw new Error('Incorrect password');
      return (await api.post(`/orders/${id}/cancel-waybill`, { remark: cancelRemark.trim() })).data;
    },
    onSuccess: () => {
      toast.success('Waybill cancelled');
      // Refresh THIS order detail + any orders list + tab counts (moving to Cancelled changes counts)
      qc.invalidateQueries({ queryKey: ['order', id] });
      qc.invalidateQueries({ predicate: (q: any) => Array.isArray(q.queryKey) && (q.queryKey[0] === 'orders' || q.queryKey[0] === 'order-tab-counts') });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Cancel failed'),
  });
  const trackingMut = useMutation({
    mutationFn: async () => (await api.get(`/orders/${id}/tracking/pull`)).data,
    onSuccess: (d: any) => {
      const msg = `Pulled ${d.eventsAdded ?? 0} new event(s)${d.unmappedCodes?.length ? ` (${d.unmappedCodes.length} unmapped)` : ''}`;
      toast.success(msg);
      qc.invalidateQueries({ queryKey: ['order', id] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Tracking pull failed'),
  });
  /**
   * Proof of delivery, via the POD service rather than `orders/:id/epod`. That route only asked the
   * courier, which answers for Delhivery B2B alone — so the button was gated to B2B Delhivery and a
   * delivered B2C order had no way to get a POD at all. The service resolves whichever proof
   * applies: the courier's signed document, our OTP certificate for own-network shipments, or a POD
   * we generate for a carrier that issues none.
   */
  const epodMut = useMutation({
    mutationFn: async () => {
      const number = (data?.orderType === 'B2B' ? (data?.lrNo || data?.awbNo) : (data?.awbNo || data?.lrNo)) ?? '';
      // Streams the POD through our API so the browser SAVES it. A presigned storage URL opened in
      // a tab instead, leaving the user to save it by hand.
      await downloadBlob(`/pod/download?orderType=${data?.orderType ?? 'B2C'}&number=${encodeURIComponent(number)}`, number || 'pod');
      return { ok: true };
    },
    onSuccess: () => {
      toast.success('POD downloaded');
      qc.invalidateQueries({ queryKey: ['order', id] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'POD download failed'),
  });

  // Geocode pickup + delivery pincodes for the route map (hook must run before any early return).
  const geo = useGeocode([data?.pickupPincode, data?.customerPincode]);

  if (isLoading) return <div className="flex justify-center py-20"><Loader2 size={24} className="animate-spin text-primary" /></div>;
  if (error || !data) return <div className="p-6 text-center text-rose-600">Order not found</div>;
  const hasNumber = !!(data.awbNo || data.lrNo);
  const canBook = !hasNumber && data.statusId !== 23;  // Ship/Book hidden when AWB/LR missing AND order is not Cancelled
  const canCancel = hasNumber && [1,2,20,21,24].includes(data.statusId);
  // Editing, two windows (mirrors OrdersService.editOrder — keep the two in step):
  //  • pre-ship (Booking Failed / Not Shipped) — anyone who may edit, any courier;
  //  • post-ship — internal staff only, and only on Gaint's own network, where the delivery person is
  //    ours and a corrected address still reaches them. An external courier is already holding the
  //    parcel, so editing our copy would only make the two records disagree.
  const preShipEdit = [21, 24].includes(data.statusId);
  const postShipEdit = !preShipEdit && !!data.isOwnCourier && !isMerchantUser && !EDIT_LOCKED_STATUSES.includes(data.statusId);
  const canEdit = preShipEdit || postShipEdit;

  const events = (data.trackingEvents ?? []).slice().sort((a,b) => new Date(b.eventDateTime).getTime() - new Date(a.eventDateTime).getTime());

  // TAT: days from Booking Received to the current status date (last event, else today).
  const currentStatusDate = data.lastEventDate ?? events[0]?.eventDateTime;
  const tat = daysBetween(data.bookingDate, currentStatusDate);
  const currentEventId = events[0]?.id;

  // Route map points (Pickup → Delivery). From popup = pickup details, To popup = customer details.
  const fromG = geo.get(data.pickupPincode);
  const toG = geo.get(data.customerPincode);
  const mapPoints: RoutePoint[] = [];
  if (fromG) mapPoints.push({
    lat: fromG.lat!, lng: fromG.lng!, kind: 'from',
    title: data.pickupPersonName || data.merchant?.brandName || data.merchant?.companyName || 'Pickup / Origin',
    subtitle: [fromG.city, fromG.state].filter(Boolean).join(', ') || `Pincode ${data.pickupPincode}`,
    lines: [data.pickupAddress ? `${data.pickupAddress}` : '', data.pickupPersonPhone ? `Ph: ${data.pickupPersonPhone}` : '', `Pincode: ${data.pickupPincode}`].filter(Boolean),
  });
  if (toG) mapPoints.push({
    lat: toG.lat!, lng: toG.lng!, kind: 'to',
    title: data.customerName || 'Delivery / Destination',
    subtitle: [data.customerCity, data.customerState, data.customerPincode].filter(Boolean).join(', '),
    lines: [data.customerAddress ? `${data.customerAddress}` : '', data.customerMobile ? `Ph: ${data.customerMobile}` : '', `Status: ${STATUS_LABEL[data.statusId] ?? data.statusId}`].filter(Boolean),
  });
  const showMap = /^\d{6}$/.test(String(data.customerPincode ?? '')) || /^\d{6}$/.test(String(data.pickupPincode ?? ''));

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      {/* Top bar. `flex-wrap` on both halves: with up to eight actions this row used to overflow on
          a laptop screen and squash the order number. The actions keep to the right and wrap onto a
          second line instead. */}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => { const b = searchParams.get('back'); nav(b ? decodeURIComponent(b) : (orderType === 'B2C' ? '/orders-b2c' : '/orders-b2b')); }}
            className="text-slate-500 hover:text-slate-900 flex items-center gap-1 text-sm whitespace-nowrap">
            <ArrowLeft size={16} /> {searchParams.get('back') === '/gaint-ofd' ? 'Gaint OFD Orders' : `Orders ${orderType}`}
          </button>
          <span className="text-slate-300">/</span>
          <h1 className="text-xl font-bold text-slate-900 truncate"><CopyText value={data.orderNo} iconSize={14} /></h1>
          <span className={`text-xs px-2 py-0.5 rounded whitespace-nowrap ${STATUS_TONE[data.statusId] ?? 'bg-slate-100 text-slate-600'}`}>
            {STATUS_LABEL[data.statusId] ?? `Status ${data.statusId}`}
          </span>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            onClick={() => nav(data.orderType === 'B2C' ? '/orders-b2c/new' : '/orders-b2b/new', { state: { duplicateFrom: data } })}
            className={`${ACTION_BTN} bg-white text-slate-700 border-slate-300 hover:bg-slate-50`}
            title="Open the create form pre-filled with this order's details (AWB/LR not copied)">
            <Copy size={14} /> Duplicate Order
          </button>
          {data.orderType === 'B2B' && data.lrNo && (
            <button onClick={() => setApptOpen(true)}
              className={`${ACTION_BTN} bg-white text-teal-700 border-teal-300 hover:bg-teal-50`}
              title="Book a delivery appointment against this LR number">
              <Calendar size={14} /> Book an Appointment
            </button>
          )}
          {canEdit && (
            <button onClick={() => setEditOpen(true)}
              className={`${ACTION_BTN} bg-white text-amber-700 border-amber-300 hover:bg-amber-50`}
              title={postShipEdit
                ? 'This shipment is on the Gaint network — customer, pickup and package details can still be corrected. A reason is required and the change is logged.'
                : 'Edit this order before it ships'}>
              <Pencil size={14} /> {postShipEdit ? 'Edit Shipped Order' : 'Edit Order'}
            </button>
          )}
          {canBook && (
            <button onClick={() => bookMut.mutate()} disabled={bookMut.isPending}
              className={`${ACTION_BTN} bg-primary text-white border-primary hover:bg-primary/90`}>
              {bookMut.isPending ? <Loader2 size={14} className="animate-spin" /> : <Ticket size={14} />} Book Waybill
            </button>
          )}
          {canCancel && (
            <button onClick={() => { setCancelPw(''); setCancelRemark(''); setCancelPwOpen(true); }} disabled={cancelMut.isPending}
              className={`${ACTION_BTN} bg-white text-rose-600 border-rose-200 hover:bg-rose-50`}>
              {cancelMut.isPending ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />} Cancel Waybill
            </button>
          )}
          {/* Try & Track — manual tracking pull, visible when AWB exists and courier supports API tracking */}
          {hasNumber && (isBrand(data.courier, 'delhivery') || isBrand(data.courier, 'dtdc')) && (
            <button onClick={() => trackingMut.mutate()} disabled={trackingMut.isPending}
              className={`${ACTION_BTN} bg-white text-indigo-600 border-indigo-200 hover:bg-indigo-50`}>
              {trackingMut.isPending ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Try &amp; Track
            </button>
          )}
          {/* Download POD — ANY delivered shipment, B2C or B2B. Was gated to B2B Delhivery, which is
              the only carrier with a POD API; every other delivered order simply had no button. */}
          {EPOD_STATUSES.includes(data.statusId) && (data.awbNo || data.lrNo) && (
            <button onClick={() => epodMut.mutate()} disabled={epodMut.isPending}
              className={`${ACTION_BTN} bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50`}>
              {epodMut.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Download POD
            </button>
          )}
          {/* Initiate Return - collect a delivered B2C parcel back. Shown on the same delivered
              statuses as the POD button, to staff AND merchants (the API pins a merchant to their
              own shipments), with no limit on how many: once a return exists, a link to the latest
              one sits beside the button rather than replacing it. */}
          {data.orderType === 'B2C' && EPOD_STATUSES.includes(data.statusId) && (
            <>
              {(data as any).returnLink?.direction === 'initiated' && (
                <Link to={`/orders-b2c/${(data as any).returnLink.order.id}`}
                  className={`${ACTION_BTN} bg-white text-amber-700 border-amber-200 hover:bg-amber-50`}>
                  <Undo2 size={14} /> Latest return: {(data as any).returnLink.order.awbNo ?? (data as any).returnLink.order.orderNo}
                </Link>
              )}
              <button onClick={() => setReturnOpen(true)}
                className={`${ACTION_BTN} bg-white text-amber-700 border-amber-200 hover:bg-amber-50`}>
                <Undo2 size={14} /> {(data as any).returnLink?.direction === 'initiated' ? 'Initiate Another Return' : 'Initiate Return'}
              </button>
            </>
          )}
          {/* Cancelled / Auto Cancelled: the button stays visible but disabled, so it is clear why
              there is no label or invoice — the server refuses them for these statuses as well. */}
          {hasNumber && [23, 33].includes(data.statusId) && (
            <button disabled title="Labels and invoices are not available for cancelled orders"
              className={`${ACTION_BTN} bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed`}>
              <Download size={14} /> Downloads <ChevronDown size={14} />
            </button>
          )}
          {hasNumber && ![9, 21, 23, 24, 33].includes(data.statusId) && (
            <DownloadDropdown orderId={data.id} orderType={data.orderType} />
          )}
          <MoneyReceiptButton orderId={data.id} systemOrderNo={data.systemOrderNo} variant="primary" size="none"
            className={ACTION_BTN} />
        </div>
      </div>

      <InitiateReturnDialog
        open={returnOpen}
        order={returnOpen ? { id: data.id, orderNo: data.orderNo, awbNo: data.awbNo, customerName: data.customerName, customerCity: data.customerCity, deliveryType: data.deliveryType } : null}
        onClose={() => setReturnOpen(false)}
        onDone={() => qc.invalidateQueries({ queryKey: ['order', id] })}
      />

      {/* Booking failure is a MESSAGE, not an action — it used to sit inline among the buttons where
          its wrapped two lines made the whole row uneven. Full width, under the header, where it can
          be read. */}
      {(data as any).lastBookingError && data.statusId === 21 && (
        <div className="text-xs px-3 py-2 mb-4 bg-rose-50 border border-rose-200 rounded-lg text-rose-700">
          <span className="font-semibold">Booking failed: </span>{(data as any).lastBookingError}
        </div>
      )}

      <BookAppointmentModal isOpen={apptOpen} onClose={() => setApptOpen(false)} lockedLrNo={data.lrNo}
        onSaved={() => toast.success('Appointment booked for LR ' + data.lrNo)} />

      <div className="grid grid-cols-3 gap-4">
        {/* Left column */}
        <div className="col-span-2 space-y-4">
          <Section title="Shipment" icon={<Truck size={16} />}>
            <Grid>
              {/* Whose shipment this is, at the top of the first card. There is a Merchant card too,
                  but it sits under the tracking timeline — which runs to 500 events — so on any
                  real shipment you never scrolled far enough to see it. Staff only: a merchant
                  looking at their own order does not need to be told who they are. */}
              {!isMerchantUser && (
                <KV label="Merchant" value={
                  <>
                    {data.merchant?.companyName ? (
                      <Link to={`/merchants/${data.merchant.id}`} className="font-bold text-slate-900 hover:text-primary hover:underline">
                        {data.merchant.companyName}
                        {data.merchant.brandName && <span className="font-normal text-slate-500"> · {data.merchant.brandName}</span>}
                      </Link>
                    ) : '—'}
                    {/* Renders only for the one account allowed to reassign — see ChangeMerchantButton. */}
                    <ChangeMerchantButton orderId={String(data.id)} orderNo={data.orderNo}
                      currentMerchantId={data.merchant?.id ?? null}
                      currentMerchantName={data.merchant?.companyName ?? null} />
                  </>
                } />
              )}
              <KV label={data.orderType === 'B2B' ? 'LR' : 'AWB'} value={
                <>
                  {(data.orderType === 'B2B' ? data.lrNo : data.awbNo)
                    ? <CopyText value={(data.orderType === 'B2B' ? data.lrNo : data.awbNo) || ''} display={<span className="font-mono">{data.orderType === 'B2B' ? data.lrNo : data.awbNo}</span>} />
                    : '—'}
                  {/* Renders only for the named accounts, and only while the shipment is still on
                      the manifest — see ChangeWaybillButton. */}
                  {!isMerchantUser && (
                    <ChangeWaybillButton orderId={String(data.id)} orderNo={data.orderNo} orderType={data.orderType as 'B2C' | 'B2B'}
                      statusId={data.statusId} awbNo={data.awbNo} lrNo={data.lrNo} />
                  )}
                </>
              } />
              {/* Re-dispatch, both ways: the old order and its waybill on the new one, and the new
                  one on the old (operator, 24-10). */}
              {(data as any).redispatchLink?.redispatchOf && (() => {
                const o = (data as any).redispatchLink.redispatchOf;
                const wb = o.orderType === 'B2B' ? (o.lrNo ?? o.awbNo) : (o.awbNo ?? o.lrNo);
                return (
                  <KV label="Re-dispatch of (old order)" value={
                    <Link to={`/orders-${String(o.orderType).toLowerCase()}/${o.id}`} className="text-primary hover:underline">
                      <b>{o.orderNo}</b>{wb ? <span className="font-mono"> · {o.orderType === 'B2B' ? 'LR' : 'AWB'} {wb}</span> : null}
                    </Link>
                  } />
                );
              })()}
              {(data as any).redispatchLink?.redispatchedTo && (() => {
                const o = (data as any).redispatchLink.redispatchedTo;
                const wb = o.orderType === 'B2B' ? (o.lrNo ?? o.awbNo) : (o.awbNo ?? o.lrNo);
                return (
                  <KV label="Re-dispatched as (new order)" value={
                    <Link to={`/orders-${String(o.orderType).toLowerCase()}/${o.id}`} className="text-primary hover:underline">
                      <b>{o.orderNo}</b>{wb ? <span className="font-mono"> · {o.orderType === 'B2B' ? 'LR' : 'AWB'} {wb}</span> : null}
                    </Link>
                  } />
                );
              })()}
              <KV label="Courier" value={data.courier?.name ? (
                <span className="inline-flex items-center gap-1 font-bold text-slate-900">
                  <Truck size={14} className="text-slate-800 shrink-0" />
                  {data.courier.name}
                </span>
              ) : '—'} />
              <KV label="Order Type" value={data.orderType} />
              <KV label="Delivery Type" value={data.deliveryType} />
              <KV label="Ship Date" value={data.shipDate ? new Date(data.shipDate).toLocaleString('en-IN') : '—'} />
              <KV label="Booking Date" value={orderDateIst(data.bookingDate)} />
              <KV label="Last Event" value={data.lastEventDate ? new Date(data.lastEventDate).toLocaleString('en-IN') : '—'} />
              <KV label="Expected Delivery (EDD)" value={data.edd ? <span data-testid="order-edd"><b className="text-slate-900">{new Date(data.edd).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</b>{(data as any).eddEstimated && <span className="ml-1 text-[11px] text-slate-500" title="Booking date + the courier's TAT — the courier has not sent an EDD for this shipment">(TAT estimate)</span>}</span> : '—'} />
              <KV label="TAT (Booking → Current)" value={tat != null ? <span className="font-semibold text-slate-900">{tat} day{tat === 1 ? '' : 's'}</span> : '—'} />
              <KV label="Customer Phone" value={data.customerMobile ? <span className="inline-flex items-center gap-2"><CopyText value={data.customerMobile} display={<span className="font-mono">{data.customerMobile}</span>} /><CallButton toNumber={data.customerMobile} orderId={data.id} awbNo={data.awbNo} lrNo={data.lrNo} reason={(data as any).statusName ?? 'Manual'} /></span> : '—'} />
              {/* Every call placed on this shipment, with the recording download. Admin only —
                  `/v1/ivr/*` is refused for a merchant token by INTERNAL_ONLY_PREFIXES. */}
              {!isMerchantUser && (
                <KV label="Calls & Recording" value={
                  <CallRecordingsButton orderId={data.id} awbNo={data.awbNo} lrNo={data.lrNo} label="View calls" prefetch />
                } />
              )}
              <KV label="Booking Source" value={<BookingSourceBadge type={(data as any).channelType} channelOrderId={(data as any).channelOrderId} />} />
              <KV label="Booked By" value={(data as any).createdBy ? `${(data as any).createdBy.firstName ?? ''} ${(data as any).createdBy.lastName ?? ''} (${(data as any).createdBy.id})`.trim() : (data as any).createdById ? `User ${(data as any).createdById}` : 'System'} />
              {data.totalBoxes && <KV label="Total Boxes" value={data.totalBoxes} />}
              {(() => {
                // Prefer the backend-resolved e-way bill (covers B2B invoiceDetails + B2C per-product),
                // falling back to the raw column.
                const eway = data.ewayBillNoResolved || data.ewayBillNo;
                return eway ? <KV label="E-Way Bill" value={<CopyText value={eway} display={<span className="font-mono">{eway}</span>} />} /> : null;
              })()}
              {data.freightCollection && <KV label="Freight" value={data.freightCollection} />}
              {data.insuranceType && <KV label="Insurance" value={data.insuranceType} />}
              {data.orderType === 'B2B' && data.jobId && (
                <KV label="Courier Job ID" value={<CopyText value={data.jobId} display={<span className="font-mono text-[11px]">{data.jobId}</span>} />} />
              )}
              {data.orderType === 'B2B' && Array.isArray(data.subAwbs) && (() => {
                const doc = data.subAwbs.find((s: any) => s.label === 'Document');
                return doc ? (
                  <KV label="Doc Waybill" value={<CopyText value={doc.awbNo} display={<span className="font-mono">{doc.awbNo}</span>} />} />
                ) : null;
              })()}
            </Grid>
          </Section>

          <Section title="Customer (Delivery Address)" icon={<UserIcon size={16} />}>
            <div className="space-y-1.5 text-sm">
              <div className="font-medium text-slate-900">{data.customerName}</div>
              <div className="text-slate-600 flex items-center gap-1.5"><Phone size={12} /> <span className="font-mono">{data.customerMobile}</span>{data.customerAltMobile ? <>· <span className="font-mono">{data.customerAltMobile}</span></> : null}</div>
              {data.customerEmail && <div className="text-slate-600 flex items-center gap-1.5"><Mail size={12} /> {data.customerEmail}</div>}
              <div className="text-slate-600 flex items-start gap-1.5"><MapPin size={12} className="mt-1 shrink-0" />
                <span>{data.customerAddress}{data.customerLandmark ? ` (${data.customerLandmark})` : ''}, {data.customerCity}, {data.customerState} — <span className="font-mono">{data.customerPincode}</span></span>
              </div>
            </div>
          </Section>

          <Section title="Pickup" icon={<Package size={16} />}>
            <div className="text-sm space-y-1.5">
              {data.pickupPersonName && <div className="font-medium">{data.pickupPersonName}{data.pickupPersonPhone ? <span className="text-slate-500 font-mono ml-2">{data.pickupPersonPhone}</span> : null}</div>}
              <div className="text-slate-600 flex items-start gap-1.5"><MapPin size={12} className="mt-1 shrink-0" />
                <span>{data.pickupAddress} — <span className="font-mono">{data.pickupPincode}</span></span>
              </div>
            </div>
          </Section>

          <Section title="Products" icon={<Package size={16} />}>
            {data.products?.length ? (() => {
              // Show a per-product E-Way Bill column only when at least one product carries one (B2C).
              const showEway = data.products.some((p: any) => (p?.ewayBillNo ?? p?.ewayBillNumber) != null && String(p?.ewayBillNo ?? p?.ewayBillNumber).trim() !== '');
              return (
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-slate-500">
                  <tr><th className="text-left py-1.5">Product</th>{showEway && <th className="text-left py-1.5">E-Way Bill</th>}<th className="text-right py-1.5">Qty</th><th className="text-right py-1.5">Weight</th><th className="text-right py-1.5">Value</th></tr>
                </thead>
                <tbody>
                  {data.products.map((p, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="py-2">{p.name ?? '—'}{p.sku ? <span className="text-xs text-slate-400 ml-2 font-mono">{p.sku}</span> : null}</td>
                      {showEway && <td className="py-2 font-mono text-xs">{(p.ewayBillNo ?? p.ewayBillNumber) ? <CopyText value={String(p.ewayBillNo ?? p.ewayBillNumber)} display={<span>{p.ewayBillNo ?? p.ewayBillNumber}</span>} iconSize={10} silent /> : '—'}</td>}
                      <td className="py-2 text-right">{p.qty ?? 1}</td>
                      <td className="py-2 text-right">{p.weight ? `${p.weight} kg` : '—'}</td>
                      <td className="py-2 text-right font-mono">₹{Number(p.value ?? 0).toLocaleString('en-IN')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              );
            })() : <div className="text-sm text-slate-400">No products listed</div>}
            <div className="border-t border-slate-100 mt-3 pt-3 grid grid-cols-3 gap-3 text-sm">
              {/* Every invoice on the consignment, not just the first — a B2B shipment routinely
                  moves against several, and the summary used to show none of them at all. */}
              {(() => {
                const d: any = data;
                const all: string[] = d.invoiceNosAll ?? (d.invoiceNo ? [d.invoiceNo] : []);
                if (!all.length) return null;
                return (
                  <KV
                    label={all.length > 1 ? `Invoice No (${all.length})` : 'Invoice No'}
                    value={<span className="font-mono text-xs">{all.join(', ')}</span>}
                  />
                );
              })()}
              <KV label="Invoice Value" value={`₹${Number(data.invoiceValue ?? 0).toLocaleString('en-IN')}`} />
              <KV label="COD Value" value={`₹${Number(data.codValue ?? 0).toLocaleString('en-IN')}`} />
              <KV label="Chargeable Wt (booked)" value={data.chargeableWeight ? `${data.chargeableWeight} kg` : '—'} />
              {data.capturedWeight != null && data.capturedWeight !== '' && (
                <KV label="Captured Wt (courier)" value={
                  <span className={Number(data.capturedWeight) > Number(data.chargeableWeight ?? 0) + 0.05 ? 'font-semibold text-rose-600' : 'text-slate-900'}>
                    {data.capturedWeight} kg
                    {Number(data.capturedWeight) > Number(data.chargeableWeight ?? 0) + 0.05 && <span className="ml-1 text-[10px] font-normal">(dispute)</span>}
                  </span>
                } />
              )}
              {/* Captured Wt is the CONSOLIDATED figure across every box, so on a multi-box B2B
                  shipment it says nothing about how much of the consignment has actually been
                  weighed — 15kg against 24 boxes reads the same whether 1 box or all 24 were
                  scanned. This states the coverage outright: how many cartons carry a scanned
                  weight, and how many carry dimensions, out of the total (the Document row is not a
                  carton and is excluded). Amber until every box is in. */}
              {data.orderType === 'B2B' && (data.subAwbs?.length ?? 0) > 0 && (() => {
                const boxes = (data.subAwbs ?? []).filter((s: any) => s.label !== 'Document');
                const weighed = boxes.filter((s: any) => s.weight != null && s.weight !== '' && Number(s.weight) > 0).length;
                const measured = boxes.filter((s: any) => s.dimensions && (s.dimensions.length || s.dimensions.breadth || s.dimensions.height)).length;
                const complete = boxes.length > 0 && weighed === boxes.length;
                return (
                  <KV label="Boxes Scanned (wt / dims)" value={
                    <span className={complete ? 'text-emerald-700 font-medium' : 'text-amber-700 font-medium'}>
                      {weighed} of {boxes.length} weighed
                      <span className="text-slate-500 font-normal"> · {measured} with dimensions</span>
                      {!complete && <span className="ml-1 text-[10px] font-normal">(pending)</span>}
                    </span>
                  } />
                );
              })()}
            </div>
          </Section>

          {/* Dimensions — B2C only (single box object). B2B uses the per-box section below. */}
          {data.dimensions && !Array.isArray(data.dimensions) && (
            <Section title="Dimensions" icon={<Package size={16} />}>
              <Grid>
                <KV label="Length" value={data.dimensions.length ? `${data.dimensions.length} cm` : '—'} />
                <KV label="Breadth" value={data.dimensions.breadth ? `${data.dimensions.breadth} cm` : '—'} />
                <KV label="Height" value={data.dimensions.height ? `${data.dimensions.height} cm` : '—'} />
                <KV label="Vol. Weight" value={data.dimensions.volWeight ? `${data.dimensions.volWeight} kg` : (data.dimensions.weight ? `${data.dimensions.weight} kg` : '—')} />
              </Grid>
            </Section>
          )}

          {/* Boxes of a multi-piece B2C parcel — one row per box, exactly what went to the courier
              (DTDC books each box separately from `Order.pieces`). B2B has its own section below. */}
          {Array.isArray((data as any).pieces) && (data as any).pieces.length > 0 && (() => {
            const boxes: any[] = (data as any).pieces;
            const volOf = (b: any) => {
              const l = Number(b.length) || 0, w = Number(b.breadth ?? b.width) || 0, h = Number(b.height) || 0;
              return (l > 0 && w > 0 && h > 0) ? (l * w * h) / 5000 : null;
            };
            const totalWeight = boxes.reduce((t, b) => t + (Number(b.weight) || 0), 0);
            const totalVol = boxes.reduce((t, b) => t + (volOf(b) ?? 0), 0);
            return (
              <Section title={`Boxes (${boxes.length})`} icon={<Package size={16} />}>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                      <tr>
                        <th className="text-left px-2 py-1">#</th>
                        <th className="text-left px-2 py-1">Description</th>
                        <th className="text-right px-2 py-1">Declared value</th>
                        <th className="text-right px-2 py-1">Weight (kg)</th>
                        <th className="text-left px-2 py-1">L × W × H (cm)</th>
                        <th className="text-right px-2 py-1">Vol. weight (kg)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {boxes.map((b: any, i: number) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="px-2 py-1">{i + 1}</td>
                          <td className="px-2 py-1">{b.description || '—'}</td>
                          <td className="px-2 py-1 text-right">{b.declaredValue != null ? Number(b.declaredValue).toLocaleString('en-IN') : '—'}</td>
                          <td className="px-2 py-1 text-right font-mono">{b.weight ?? '—'}</td>
                          <td className="px-2 py-1 font-mono">{[b.length, b.breadth ?? b.width, b.height].filter((v: any) => v != null).join(' × ') || '—'}</td>
                          <td className="px-2 py-1 text-right font-mono">{volOf(b) != null ? (volOf(b) as number).toFixed(3) : '—'}</td>
                        </tr>
                      ))}
                      <tr className="border-t border-slate-200 font-semibold">
                        <td className="px-2 py-1" colSpan={3}>Total</td>
                        <td className="px-2 py-1 text-right font-mono">{totalWeight.toFixed(3)}</td>
                        <td className="px-2 py-1" />
                        <td className="px-2 py-1 text-right font-mono">{totalVol.toFixed(3)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </Section>
            );
          })()}

          {/* B2B per-box Dimensions (boxes) + Sub-orders (line items, shown separately) */}
          {data.orderType === 'B2B' && (Array.isArray((data as any).subOrders) && (data as any).subOrders.length > 0 || Array.isArray((data as any).dimensions) && (data as any).dimensions.length > 0) && (() => {
            const so: any[] = Array.isArray((data as any).subOrders) ? (data as any).subOrders : [];
            const dimArr: any[] = Array.isArray((data as any).dimensions) ? (data as any).dimensions : [];
            // Boxes are the dimension rows (each = qty boxes of that size). Sub-orders are independent line items.
            const boxSpecs = dimArr.length > 0 ? dimArr : so;
            const lineItems = so.filter((s: any) => s.description || s.clientRefId);
            const totalBoxes = boxSpecs.reduce((sum: number, b: any) => sum + (Number(b.qty) || 1), 0);
            // Per-box courier AWBs (Master/Child rows from subAwbs), indexed by boxNo (1-based).
            const boxAwbByNo: Record<number, string> = {};
            (data.subAwbs ?? []).filter((a: any) => a.label !== 'Document' && !a.isMaster).forEach((a: any) => { boxAwbByNo[a.boxNo] = a.awbNo; });

            // The volumetric weight the merchant/user entered at booking was captured but never
            // shown: the table printed L, B, H and the raw "L × B × H" string and stopped there, so
            // the one figure that decides what gets billed on a large light consignment was
            // invisible on the page where the shipment is investigated.
            //
            // The divisor and the totals come from the BILLING code (`weightBreakdown`), not from a
            // constant and not from `courier.volumetricDivisor`. B2B bills on the PRICING PLAN's
            // divisor (usually 4500) while the courier row says 4000 — dividing by the courier's
            // number showed 79.607 kg against an invoice of 70.762 kg, which is exactly the
            // disagreement this page is opened to settle.
            const wb = (data as any).weightBreakdown as
              { divisor: number; minChargeWeight: number | null; dead: number; volumetric: number; captured: number | null; chargeable: number; billed: number | null } | null;
            const divisor = Number(wb?.divisor) || 5000;
            const volOf = (s: any) => {
              const l = Number(s.length) || 0, b = Number(s.breadth ?? s.width) || 0, h = Number(s.height) || 0;
              if (!(l > 0 && b > 0 && h > 0)) return null;
              return (l * b * h) / divisor;
            };
            // Per-box figures are for reading the consignment; the TOTALS are the billing engine's
            // own, so the footer can never disagree with the invoice by a rounding step.
            const totalVol = wb?.volumetric ?? boxSpecs.reduce((sum: number, s: any) => sum + (volOf(s) ?? 0) * (Number(s.qty) || 1), 0);
            const totalDead = wb?.dead ?? boxSpecs.reduce((sum: number, s: any) => sum + (Number(s.weight) || 0) * (Number(s.qty) || 1), 0);
            const billed = wb?.billed ?? null;
            const kg = (n: number) => `${n.toFixed(3)} kg`;

            return (
              <Section title={`Per-box Dimensions (${totalBoxes} box${totalBoxes !== 1 ? 'es' : ''})`} icon={<Package size={16} />}>
                {lineItems.length > 0 && (
                  <div className="mb-3 text-xs text-slate-600 space-y-0.5">
                    <div className="uppercase text-slate-400">Sub-orders (line items)</div>
                    {lineItems.map((s: any, i: number) => (
                      <div key={i}>• {s.description || '—'}{s.clientRefId ? <span className="font-mono text-slate-400"> · {s.clientRefId}</span> : null}{s.qty ? <span> · qty {s.qty}</span> : null}</div>
                    ))}
                  </div>
                )}
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-slate-500">
                    <tr>
                      <th className="text-left py-1.5">Box AWB</th>
                      <th className="text-left py-1.5">Qty</th>
                      <th className="text-left py-1.5">L (cm)</th>
                      <th className="text-left py-1.5">B (cm)</th>
                      <th className="text-left py-1.5">H (cm)</th>
                      <th className="text-left py-1.5">Volume (L×B×H)</th>
                      <th className="text-right py-1.5">Dead Wt</th>
                      <th className="text-right py-1.5">Vol. Wt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {boxSpecs.map((s: any, idx: number) => {
                      const br = s.breadth ?? s.width;
                      const vol = (s.length && br && s.height) ? `${s.length} × ${br} × ${s.height}` : '—';
                      const vw = volOf(s);
                      const dw = Number(s.weight) || 0;
                      return (
                        <tr key={idx} className="border-t border-slate-100">
                          <td className="py-2 font-mono text-xs">{boxAwbByNo[idx + 1] ? <CopyText value={boxAwbByNo[idx + 1]} display={<span>{boxAwbByNo[idx + 1]}</span>} iconSize={10} silent /> : '—'}</td>
                          <td className="py-2">{s.qty ?? 1}</td>
                          <td className="py-2">{s.length ?? '—'}</td>
                          <td className="py-2">{br ?? '—'}</td>
                          <td className="py-2">{s.height ?? '—'}</td>
                          <td className="py-2 text-xs text-slate-500">{vol}</td>
                          <td className="py-2 text-right font-mono text-xs">{dw ? kg(dw) : '—'}</td>
                          {/* Highlighted when volumetric beats dead on this box — that is the case
                              the whole measurement exists to catch. */}
                          <td className={`py-2 text-right font-mono text-xs ${vw != null && vw > dw ? 'text-violet-700 font-semibold' : ''}`}>
                            {vw != null ? kg(vw) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="text-xs">
                    <tr className="border-t-2 border-slate-200">
                      <td colSpan={6} className="py-2 text-right text-slate-500 uppercase">Total ({totalBoxes} boxes, ÷{divisor})</td>
                      <td className="py-2 text-right font-mono font-semibold">{kg(totalDead)}</td>
                      <td className={`py-2 text-right font-mono font-semibold ${totalVol > totalDead ? 'text-violet-700' : ''}`}>{kg(totalVol)}</td>
                    </tr>
                  </tfoot>
                </table>
                <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs">
                  <span className="text-slate-500">
                    Chargeable weight is the highest of dead, volumetric and any weight the courier reports
                    {wb?.minChargeWeight ? `, floored at the plan's ${wb.minChargeWeight} kg minimum` : ''}.
                  </span>
                  <span>Booked: <b className="font-mono">{data.chargeableWeight ? `${Number(data.chargeableWeight).toFixed(3)} kg` : '—'}</b></span>
                  {wb?.captured != null && (
                    <span>Courier reported: <b className="font-mono">{kg(wb.captured)}</b></span>
                  )}
                  {billed != null && (
                    <span>
                      Billed: <b className={`font-mono ${billed > Number(data.chargeableWeight ?? 0) + 0.001 ? 'text-rose-600' : ''}`}>{kg(billed)}</b>
                    </span>
                  )}
                  {/* Says so out loud when the shipment has not been re-priced since its weight
                      moved, rather than showing two numbers and leaving the reader to spot it. */}
                  {wb && billed != null && Math.abs(wb.chargeable - billed) > 0.001 && (
                    <span className="text-amber-700">
                      Would re-price to <b className="font-mono">{kg(wb.chargeable)}</b> — run Freight → Recalculate.
                    </span>
                  )}
                </div>
              </Section>
            );
          })()}

          {/* B2B Invoice Details — parsed from userRemarks JSON */}
          {data.orderType === 'B2B' && (() => {
            try {
              const parsed = data.userRemarks ? JSON.parse(data.userRemarks) : null;
              const inv = parsed?.invoiceDetails;
              if (Array.isArray(inv) && inv.length > 0) {
                return (
                  <Section title={`Invoice Details (${inv.length})`} icon={<FileText size={16} />}>
                    <table className="w-full text-sm">
                      <thead className="text-xs uppercase text-slate-500">
                        <tr>
                          <th className="text-left py-1.5">Invoice #</th>
                          <th className="text-left py-1.5">Date</th>
                          <th className="text-left py-1.5">Product</th>
                          <th className="text-left py-1.5">HSN</th>
                          <th className="text-left py-1.5">E-Way Bill</th>
                          <th className="text-left py-1.5">Qty</th>
                          <th className="text-left py-1.5">Value (₹)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inv.map((iv: any, i: number) => (
                          <tr key={i} className="border-t border-slate-100">
                            <td className="py-2 font-mono text-xs">{iv.invoiceNumber ?? iv.invNo ?? '—'}</td>
                            <td className="py-2 text-xs">{iv.invoiceDate ?? '—'}</td>
                            <td className="py-2">{iv.productInvoiceName ?? iv.productName ?? '—'}</td>
                            <td className="py-2 font-mono text-xs">{iv.hsnCode ?? '—'}</td>
                            <td className="py-2 font-mono text-xs">{(iv.ewayBillNumber ?? iv.ewayBillNo) ? <CopyText value={String(iv.ewayBillNumber ?? iv.ewayBillNo)} display={<span>{iv.ewayBillNumber ?? iv.ewayBillNo}</span>} iconSize={10} silent /> : '—'}</td>
                            <td className="py-2">{iv.qty ?? '—'}</td>
                            <td className="py-2 font-mono">{Number(iv.productInvoiceValue ?? 0).toLocaleString('en-IN')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Section>
                );
              }
            } catch {}
            return null;
          })()}

          {/* B2B-only block: sub-AWBs / boxes with per-box scan history */}
          {data.orderType === 'B2B' && (data.subAwbs?.length ?? 0) > 0 && (
            <Section title={`Boxes & AWBs (${data.subAwbs!.length})`} icon={<Package size={16} />}>
              <BoxAwbTable rows={data.subAwbs!} />
            </Section>
          )}

          <WeightDisputeSection orderId={String(data.id)} />
        </div>

        {/* Right column — Tracking timeline */}
        <div className="space-y-4">
          {showMap && (
            <RouteMapCard points={mapPoints} title="Shipment Route (Pickup → Delivery)" loading={geo.isLoading}
              unavailableText="Route map not available for these pincodes" previewHeight={170} />
          )}
          <Section title="Tracking Timeline" icon={<Calendar size={16} />}>
            <div className="flex flex-wrap gap-2 mb-3 text-xs">
              {tat != null && (
                <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 px-2 py-1 rounded">
                  <Calendar size={11} /> TAT: <b>{tat} day{tat === 1 ? '' : 's'}</b> <span className="text-slate-400">(booking → current)</span>
                </span>
              )}
              {data.edd && (
                <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-1 rounded">
                  <Calendar size={11} /> EDD: <b>{new Date(data.edd).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</b>
                </span>
              )}
            </div>
            {events.length === 0 ? (
              <div className="text-sm text-slate-400 py-4 text-center">No tracking events yet</div>
            ) : (
              <ol className="relative border-l border-slate-200 ml-2 space-y-4">
                {events.map(ev => (
                  <li key={ev.id} className="ml-4">
                    {ev.id === currentEventId ? (
                      <span className="absolute -left-[7px] flex h-3.5 w-3.5" title="Current status">
                        <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${STATUS_TONE[ev.statusId]?.split(' ')[0] ?? 'bg-slate-400'}`} />
                        <span className={`relative inline-flex rounded-full h-3.5 w-3.5 ${STATUS_TONE[ev.statusId]?.split(' ')[0] ?? 'bg-slate-400'}`} />
                      </span>
                    ) : (
                      <div className={`absolute -left-[5px] w-2.5 h-2.5 rounded-full ${STATUS_TONE[ev.statusId]?.split(' ')[0] ?? 'bg-slate-300'}`}></div>
                    )}
                    <div className="text-xs text-slate-500">{new Date(ev.eventDateTime).toLocaleString('en-IN')}</div>
                    <div className="mt-0.5">
                      <span className={`inline-block text-sm font-semibold px-2 py-0.5 rounded ${STATUS_TONE[ev.statusId] ?? 'bg-slate-100 text-slate-700'}`}>
                        {STATUS_LABEL[ev.statusId] ?? `Status ${ev.statusId}`}
                      </span>
                    </div>
                    {ev.statusMessage && <div className="text-xs text-slate-600">{ev.statusMessage}</div>}
                    {/* `location` is the courier's own place string and is set on ~90% of scans;
                        the structured columns are set on under 2%, so showing only those left nine
                        out of ten events with no location at all. */}
                    {(ev.location || ev.scanCity || ev.scanState) && (
                      <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><MapPin size={10} /> {ev.location || [ev.scanCity, ev.scanState, ev.scanPincode].filter(Boolean).join(', ')}</div>
                    )}
                    {ev.receiverName && (
                      <div className="text-xs text-emerald-700 mt-0.5 flex items-center gap-1">
                        <CheckCircle2 size={10} /> Received by: {ev.receiverName}{ev.receiverPhone ? ` · ${ev.receiverPhone}` : ''}
                      </div>
                    )}
                    {ev.podFile && (
                      <SignedFileLink href={ev.podFile} download downloadName={((data?.orderType === 'B2B' ? (data?.lrNo || data?.awbNo) : (data?.awbNo || data?.lrNo)) || 'pod') as string}
                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-0.5">
                        <Download size={10} /> Download POD
                      </SignedFileLink>
                    )}
                    {/* Who made this scan, beside how it arrived. The API sends `updatedBy` only to
                        internal staff and only for a MANUAL event - a courier poll has no actor -
                        so the absence of it is the gate; there is nothing to check here. */}
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      <span className="uppercase">{ev.receivedVia}</span>
                      {(ev as any).updatedBy && (
                        <span className="normal-case text-slate-500"> · by {(ev as any).updatedBy}</span>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </Section>

          <Section title="Merchant" icon={<MessageSquare size={16} />}>
            <div className="text-sm">
              <div className="font-medium text-slate-900">{data.merchant?.companyName ?? '—'}</div>
              {data.merchant?.brandName && <div className="text-slate-500 text-xs">{data.merchant.brandName}</div>}
            </div>
          </Section>
        </div>
      </div>
      <CancelDispatchPanel order={data} canEdit={!isMerchantUser} />
      <OrderRemarks orderId={String(data.id)} />
      {/* Edit history — internal staff and admins only. Merchants never see who on our team touched
          their order, or why; the API refuses them the route as well, this is not the only guard. */}
      {!isMerchantUser && <OrderEditHistory orderId={String(data.id)} />}
      {editOpen && (
        <EditOrderModal
          order={data}
          postShip={postShipEdit}
          onClose={() => { setEditOpen(false); if (searchParams.get('edit') === '1') { searchParams.delete('edit'); setSearchParams(searchParams, { replace: true }); } }}
          onSaved={() => {
            setEditOpen(false);
            qc.invalidateQueries({ queryKey: ['order', id] });
            qc.invalidateQueries({ queryKey: ['order-edit-logs', id] });
            toast.success('Order updated');
          }}
        />
      )}
    {cancelPwOpen && (
        <Modal isOpen onClose={() => setCancelPwOpen(false)} title="Confirm Cancellation">
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Reason for cancellation <span className="text-rose-600">*</span></label>
              <textarea data-testid="cancel-remark" autoFocus rows={3} value={cancelRemark} onChange={(e) => setCancelRemark(e.target.value)}
                placeholder="Why is this shipment being cancelled?"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-primary" />
            </div>
            <p className="text-sm text-slate-600">Re-enter your login password to confirm.</p>
            <input
              type="password"
              data-testid="cancel-password"
              placeholder="Your password"
              value={cancelPw}
              onChange={(e) => setCancelPw(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-primary"
            />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => { setCancelPwOpen(false); setCancelPw(''); }}
                className="px-3 py-1.5 border border-slate-300 text-slate-700 text-sm rounded-lg hover:bg-slate-50">Back</button>
              <button type="button" data-testid="cancel-confirm" disabled={!cancelPw || cancelRemark.trim().length < 3 || cancelBusy} onClick={() => {
                setCancelBusy(true);
                cancelMut.mutate(cancelPw, { onSettled: () => { setCancelBusy(false); setCancelPwOpen(false); setCancelPw(''); } });
              }} className="bg-danger text-white px-3 py-1.5 text-sm rounded-lg disabled:opacity-50">
                {cancelBusy ? 'Verifying…' : 'Confirm Cancel'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Weight Disputes (per-order) ───
interface WeightDisputeRow {
  id: number; awbNo?: string | null; lrNo?: string | null; subAwb?: string | null;
  originalWeight: string | number; newWeight: string | number; diffWeight: string | number;
  volumetricWeight?: string | number | null; weightType?: string; source?: string;
  status: 'NEW' | 'PENDING' | 'ACCEPTED' | 'AUTO_ACCEPTED' | 'REJECTED'; reason?: string | null; resolutionNote?: string | null;
  capturedAt?: string;
  /** Who accepted / rejected it and when — internal screens only. */
  resolvedByName?: string | null; resolvedAt?: string | null;
}
interface WeightDisputeData {
  counts: { new: number; pending: number; accepted: number; rejected: number; total: number };
  totalKgDiff: number; items: WeightDisputeRow[];
}
const DISPUTE_BADGE: Record<string, string> = {
  NEW: 'bg-amber-100 text-amber-700', PENDING: 'bg-blue-100 text-blue-700',
  ACCEPTED: 'bg-emerald-100 text-emerald-700', AUTO_ACCEPTED: 'bg-slate-200 text-slate-700', REJECTED: 'bg-rose-100 text-rose-700',
};

function DisputeStat({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div className={`rounded-lg border p-2.5 ${tone}`}>
      <div className="text-2xl font-bold leading-none">{value}</div>
      <div className="text-[11px] mt-1 leading-tight">{label}</div>
    </div>
  );
}

function WeightDisputeSection({ orderId }: { orderId: string }) {
  const qc = useQueryClient();
  const user = useAuth((s) => s.user);
  const isStaff = user?.kind !== 'merchant';
  const { data } = useQuery({
    queryKey: ['weight-order', orderId],
    enabled: !!orderId,
    queryFn: async () => (await api.get<WeightDisputeData>(`/weight/order/${orderId}`)).data,
  });
  const act = useMutation({
    mutationFn: async ({ id, action }: { id: number; action: 'dispute' | 'accept' | 'reject' }) =>
      (await api.post(`/weight/${id}/${action}`)).data,
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ['weight-order', orderId] });
      // Accept / reject moves the order's weight and re-prices its freight — reload both.
      if (v.action !== 'dispute') { qc.invalidateQueries({ queryKey: ['order'] }); qc.invalidateQueries({ queryKey: ['freight'] }); }
      toast.success(v.action === 'accept' ? 'Weight accepted — order weight and freight updated' : 'Weight dispute updated');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Update failed'),
  });
  const c = data?.counts ?? { new: 0, pending: 0, accepted: 0, rejected: 0, total: 0 };
  const items = data?.items ?? [];

  return (
    <Section title="Weight Disputes" icon={<Package size={16} />}>
      <div className="grid grid-cols-4 gap-2 mb-3">
        <DisputeStat label="New Discrepancies" value={c.new} tone="bg-amber-50 text-amber-700 border-amber-200" />
        <DisputeStat label="Pending Resolution" value={c.pending} tone="bg-blue-50 text-blue-700 border-blue-200" />
        <DisputeStat label="Accepted by Courier" value={c.accepted} tone="bg-emerald-50 text-emerald-700 border-emerald-200" />
        <DisputeStat label="Rejected by Courier" value={c.rejected} tone="bg-rose-50 text-rose-700 border-rose-200" />
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-slate-400">No weight discrepancies recorded for this order.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left py-1.5">AWB / Sub-AWB</th>
              <th className="text-right py-1.5">Original</th>
              <th className="text-right py-1.5">New</th>
              <th className="text-right py-1.5">Diff</th>
              <th className="text-left py-1.5 pl-3">Type / Source</th>
              <th className="text-left py-1.5">Status</th>
              {isStaff && <th className="text-right py-1.5">Action</th>}
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 align-top">
                <td className="py-2 font-mono text-xs">{r.subAwb || r.awbNo || r.lrNo || '—'}</td>
                <td className="py-2 text-right">{Number(r.originalWeight).toFixed(3)} kg</td>
                <td className="py-2 text-right">{Number(r.newWeight).toFixed(3)} kg</td>
                <td className="py-2 text-right font-medium text-rose-600">+{Number(r.diffWeight).toFixed(3)} kg</td>
                <td className="py-2 pl-3 text-xs text-slate-500">{r.weightType ?? 'DEAD'} · {r.source ?? 'UPLOAD'}</td>
                <td className="py-2">
                  <span className={`text-xs px-2 py-0.5 rounded ${DISPUTE_BADGE[r.status] ?? 'bg-slate-100 text-slate-600'}`}>{r.status === 'AUTO_ACCEPTED' ? 'AUTO ACCEPTED' : r.status}</span>
                  {(r.resolvedByName || r.resolvedAt) && (
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {r.resolvedByName ? `${r.status === 'AUTO_ACCEPTED' ? '' : 'by '}${r.resolvedByName}` : ''}
                      {r.resolvedByName && r.resolvedAt ? ' · ' : ''}
                      {r.resolvedAt ? new Date(r.resolvedAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                    </div>
                  )}
                  {r.resolutionNote && <div className="text-[11px] text-slate-400 mt-0.5">{r.resolutionNote}</div>}
                  {isStaff && r.status === 'NEW' && (
                    <div className="text-[11px] text-amber-700 mt-0.5 max-w-[220px]">
                      Not billed yet — freight stays on {Number(r.originalWeight).toFixed(3)} kg until accepted
                      {r.capturedAt ? ` (auto-accept ${new Date(new Date(r.capturedAt).getTime() + 48 * 3600_000).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })})` : ''}.
                    </div>
                  )}
                </td>
                {isStaff && (
                  <td className="py-2 text-right whitespace-nowrap">
                    {r.status === 'NEW' && (
                      <div className="inline-flex gap-1">
                        {/* An uploaded / courier weight is only a claim until accepted — the order's
                            weight and the freight move on accept (or on the 48 h auto-accept). */}
                        <button onClick={() => { if (window.confirm(`Accept ${Number(r.newWeight).toFixed(3)} kg? The order's weight changes and its freight is re-priced now.`)) act.mutate({ id: r.id, action: 'accept' }); }}
                          disabled={act.isPending}
                          className="text-xs px-2 py-1 border border-emerald-300 text-emerald-700 rounded hover:bg-emerald-50 disabled:opacity-50 inline-flex items-center gap-1"><CheckCircle2 size={11} /> Accept</button>
                        <button onClick={() => act.mutate({ id: r.id, action: 'dispute' })} disabled={act.isPending}
                          className="text-xs px-2 py-1 border border-blue-300 text-blue-700 rounded hover:bg-blue-50 disabled:opacity-50">Raise Dispute</button>
                      </div>
                    )}
                    {r.status === 'PENDING' && (
                      <div className="inline-flex gap-1">
                        <button onClick={() => act.mutate({ id: r.id, action: 'accept' })} disabled={act.isPending}
                          className="text-xs px-2 py-1 border border-emerald-300 text-emerald-700 rounded hover:bg-emerald-50 disabled:opacity-50 inline-flex items-center gap-1"><CheckCircle2 size={11} /> Accepted</button>
                        <button onClick={() => act.mutate({ id: r.id, action: 'reject' })} disabled={act.isPending}
                          className="text-xs px-2 py-1 border border-rose-300 text-rose-700 rounded hover:bg-rose-50 disabled:opacity-50 inline-flex items-center gap-1"><XCircle size={11} /> Rejected</button>
                      </div>
                    )}
                    {(r.status === 'ACCEPTED' || r.status === 'AUTO_ACCEPTED' || r.status === 'REJECTED') && <span className="text-xs text-slate-400">Resolved</span>}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}

interface EditOrderModalProps { order: OrderDetail; postShip?: boolean; onClose: () => void; onSaved: () => void; }
function EditOrderModal({ order, postShip = false, onClose, onSaved }: EditOrderModalProps) {
  // A merchant is already scoped to themselves — the backend strips `merchantId` from their PATCH —
  // so showing them a "Select Merchant" picker is meaningless and implies they could move the order.
  const authUser = useAuth((s) => s.user);
  const isStaffUser = authUser?.kind !== 'merchant';
  // Post-ship edit (own network only): the parcel is out, so the fields that would re-point it —
  // merchant, courier, order no, LR/AWB — are locked. The backend refuses a real change to any of
  // them anyway; disabling them here means the user finds that out before typing, not after saving.
  // …except for the accounts allowed to re-point a booked shipment. Same server answer the
  // standalone Change control uses, so the form and the API agree on who may do this.
  const { data: repointGate } = useQuery({
    queryKey: ['order-merchant-reassign-allowed'],
    queryFn: async () => (await api.get<{ allowed: boolean }>('/orders/merchant-reassign/allowed')).data,
    staleTime: 10 * 60_000,
    retry: false,
    enabled: isStaffUser,
  });
  const lockRepoint = postShip;
  const lockMerchant = postShip && !repointGate?.allowed;
  const [editReason, setEditReason] = useState('');
  const qc = useQueryClient();
  const isB2B = order.orderType === 'B2B';
  const _rawDim = (order.dimensions as any) ?? {};
  // For B2C: dimensions is an object { length, breadth, height, volWeight }
  // For B2B: dimensions is an ARRAY of per-box rows [{ qty, length, breadth, height, weight }, ...]
  // Treat it accordingly so the form picks up the right initial values.
  const dim: any = Array.isArray(_rawDim) ? (_rawDim[0] ?? {}) : _rawDim;
  const _rawSub: any[] = Array.isArray((order as any).subOrders) ? (order as any).subOrders : [];
  const _rawDimArr: any[] = Array.isArray(_rawDim) ? _rawDim : [];
  // Build the per-box edit rows from BOTH arrays without dropping any. Boxes come from dimensions[]
  // (each row = qty boxes of that size); the sub-order description/clientRef rides along on the
  // matching row. Use max length so extra box rows are never lost, and take qty/L/B/H/W from the
  // DIMENSIONS array (not the sub-order qty) so box counts aren't conflated with line-item qty.
  const _mergeN = Math.max(_rawSub.length, _rawDimArr.length);
  const _mergedSub = Array.from({ length: _mergeN }, (_, i) => {
    const s: any = _rawSub[i] ?? {};
    const d: any = _rawDimArr[i] ?? {};
    return {
      description: s.description ?? '',
      clientRefId: s.clientRefId ?? '',
      qty: Number(d.qty ?? s.qty ?? 1) || 1,
      length: Number(d.length ?? s.length ?? 0) || 0,
      breadth: Number(d.breadth ?? s.breadth ?? 0) || 0,
      height: Number(d.height ?? s.height ?? 0) || 0,
      weight: Number(d.weight ?? s.weight ?? 0) || 0,
    };
  });
  const initialSubOrders = _mergedSub.length > 0 ? _mergedSub : (isB2B ? [{ description: '', clientRefId: '', qty: 1, length: 0, breadth: 0, height: 0, weight: 0 }] : []);
  const initialProducts = Array.isArray((order as any).products) && (order as any).products.length
    ? (order as any).products
    : [{ name: '', sku: '', qty: 1, value: 0, hsn: '' }];

  // ── Customer
  const [orderNo, setOrderNo] = useState(order.orderNo ?? '');
  const [name, setName] = useState(order.customerName ?? '');
  const [mobile, setMobile] = useState(order.customerMobile ?? '');
  const [altMobile, setAlt] = useState(order.customerAltMobile ?? '');
  const [email, setEmail] = useState(order.customerEmail ?? '');
  const [addr, setAddr] = useState(order.customerAddress ?? '');
  const [landmark, setLandmark] = useState((order as any).customerLandmark ?? '');
  const [pincode, setPincode] = useState(order.customerPincode ?? '');
  const [city, setCity] = useState(order.customerCity ?? '');
  const [state, setState] = useState(order.customerState ?? '');

  // ── Pickup
  const [warehouseId, setWid] = useState<number | ''>(((order as any).warehouseId ?? '') as any);
  const [pickupAddress, setPa] = useState((order as any).pickupAddress ?? '');
  const [pickupPincode, setPp] = useState((order as any).pickupPincode ?? '');
  const [pickupPersonName, setPpn] = useState((order as any).pickupPersonName ?? '');
  const [pickupPersonPhone, setPph] = useState((order as any).pickupPersonPhone ?? '');

  // ── Payment + invoice
  const [deliveryType, setDeliveryType] = useState<'COD' | 'PREPAID'>(order.deliveryType || 'PREPAID');
  const [codValue, setCod] = useState(Number(order.codValue ?? 0));
  const [invoiceValue, setInv] = useState(Number(order.invoiceValue ?? 0));
  const [ewayBillNo, setEway] = useState((order as any).ewayBillNo ?? '');

  // ── Package
  const [chargeableWeight, setWt] = useState(order.chargeableWeight ?? '');
  const [lengthCm, setLen] = useState(Number(dim.length ?? 0));
  const [widthCm, setBr] = useState(Number(dim.breadth ?? 0));
  const [heightCm, setHei] = useState(Number(dim.height ?? 0));
  // For B2B, total boxes = sum of sub-orders qty (single source of truth, matches Create form behavior).
  // For B2C, use the order's persisted totalBoxes (or 1).
  const _b2bSumBoxes = isB2B ? _mergedSub.reduce((sum: number, s: any) => sum + (Number(s.qty) || 0), 0) : 0;
  const [totalBoxes, setBoxes] = useState(
    isB2B
      ? (_b2bSumBoxes > 0 ? _b2bSumBoxes : Number((order as any).totalBoxes ?? 1))
      : Number((order as any).totalBoxes ?? 1)
  );

  // ── Courier
  const [courierId, setCourierId] = useState<number | ''>(((order as any).courierId ?? '') as any);
  // Merchant picker (Edit B2B parity with Create — was missing).
  // Stored in form state so admin can re-assign the order to a different merchant.
  const [merchantId, setMerchantId] = useState<number | ''>(((order as any).merchantId ?? '') as any);

  // ── Products array
  const [products, setProducts] = useState<any[]>(initialProducts);

  // ── B2B specifics
  const [insuranceType, setIns] = useState((order as any).insuranceType ?? 'OWNER_RISK');
  const [freightCollection, setFc] = useState((order as any).freightCollection ?? '');
  const [pickupType, setPt] = useState((order as any).pickupType ?? '');
  // LR Generate Type (Booking Type): '1'=Automatic, '2'=Manual
  const [lrGenerateType, setLrGenType] = useState<'1' | '2'>(
    // The recorded booking type when there is one (orders from 28 Oct 2026); older orders keep the old guess.
    (order as any).bookingType === 'MANUAL' ? '2' : (order as any).bookingType === 'AUTOMATIC' ? '1'
      : ((order as any).lrGenerateType as '1' | '2') ?? ((order.lrNo || (order as any).manualLr) ? '2' : '1')
  );
  const [lrNo, setLrNo] = useState((order as any).lrNo ?? '');
  // B2B sub-orders: { description, clientRefId, qty, length, breadth, height, weight }
  const [subOrders, setSubOrders] = useState<any[]>(initialSubOrders);
  // B2B product info (matches Create form's single-row shape)
  const initialB2bProduct = (initialProducts[0] ?? {}) as any;
  const [b2bProductName, setB2bProductName] = useState<string>(initialB2bProduct.name ?? '');
  const [b2bProjectName, setB2bProjectName] = useState<string>(initialB2bProduct.project ?? '');
  const [b2bTotalWeight, setB2bTotalWeight] = useState<number>(Number(initialB2bProduct.weight ?? order.chargeableWeight ?? 0));
  const [b2bIsFragile, setB2bIsFragile] = useState<'Yes' | 'No' | ''>(initialB2bProduct.isFragile === true ? 'Yes' : 'No');

  // ── User remarks (free-text only — invoiceDetails JSON lives separately).
  // If the persisted value is a JSON blob (because we put invoiceDetails there),
  // extract just the .freeText for the textarea so the user doesn't see JSON noise.
  const _initialFreeText: string = (() => {
    const raw = (order as any).userRemarks;
    if (!raw) return '';
    if (typeof raw !== 'string') return '';
    const trimmed = raw.trim();
    // If it looks like JSON we wrote, parse and grab freeText; otherwise return raw as-is.
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed && typeof parsed === 'object') {
          return typeof parsed.freeText === 'string' ? parsed.freeText : '';
        }
      } catch { /* not JSON — treat as plain text below */ }
    }
    return raw;
  })();
  const [userRemarks, setUR] = useState<string>(_initialFreeText);

  // ── B2B Invoice Details (array). Stored inside userRemarks JSON under .invoiceDetails by the Create form.
  // We parse the existing value so Edit can show + modify them, then re-serialize on save.
  const _initialInvoiceDetails: any[] = (() => {
    try {
      const raw = (order as any).userRemarks;
      if (!raw) return [{}];
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      const arr = parsed?.invoiceDetails;
      if (Array.isArray(arr) && arr.length > 0) return arr;
    } catch { /* not JSON — fine, fall back to single empty row */ }
    return [{}];
  })();
  const [invoiceDetails, setInvoiceDetails] = useState<any[]>(_initialInvoiceDetails);
  const updateInvoice = (i: number, patch: any) => setInvoiceDetails(rows => rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const addInvoice = () => setInvoiceDetails(rows => [...rows, {}]);
  const removeInvoice = (i: number) => setInvoiceDetails(rows => rows.filter((_, idx) => idx !== i));

  // Pincode → city/state autofill (when user types a fresh pin)
  usePincodeAutofill(pincode, (d: any) => { if (!city) setCity(d.city); if (!state) setState(d.state); });
  usePincodeAutofill(pickupPincode, (_d: any) => { /* pickup state/city not separate fields in edit — noop */ });

  const { data: statesList = [] } = useQuery({
    queryKey: ['states'],
    queryFn: async () => (await api.get('/states')).data as Array<{ id: number; name: string }>,
  });
  // Case-insensitive normalize: if the stored state name differs in case from the dropdown,
  // pick the canonical option so the <select> shows it as selected.
  useEffect(() => {
    if (!state || !statesList.length) return;
    const exact = statesList.find((s: any) => s.name === state);
    if (exact) return;
    const ci = statesList.find((s: any) => s.name.toLowerCase().trim() === state.toLowerCase().trim());
    if (ci) setState(ci.name);
  }, [statesList, state]);
  const { data: warehousesData = [] } = useQuery({
    queryKey: ['merchant-warehouses', (order as any).merchantId],
    enabled: !!(order as any).merchantId,
    // /merchants/:id/warehouses already merges merchant-own + active master warehouses
    // (masters are prefixed 'Gaint Master · …')
    queryFn: async () => (await api.get(`/merchants/${(order as any).merchantId}/warehouses`, { params: { includeMaster: 'true' } })).data as any[],
  });
  // Ensure the order's currently-saved warehouse always appears in the dropdown,
  // even if it has been deactivated since the order was placed.
  const warehouses = (() => {
    const list: any[] = Array.isArray(warehousesData) ? [...warehousesData] : [];
    const cur = (order as any).warehouseId;
    if (cur && !list.find((w: any) => Number(w.id) === Number(cur))) {
      list.unshift({ id: cur, name: '(currently saved warehouse #' + cur + ')', isActive: false });
    }
    return list;
  })();

  // When user changes the warehouse, auto-fill pickup address / pincode / person / phone
  const onWarehouseChange = (newId: number | '') => {
    setWid(newId);
    if (!newId) return;
    const w: any = warehouses.find((x: any) => Number(x.id) === Number(newId));
    if (!w) return;
    if (w.address) setPa(w.address);
    if (w.pincode) setPp(String(w.pincode));
    if (w.contactName) setPpn(w.contactName);
    if (w.contactPhone) setPph(w.contactPhone);
  };
  // Merchants list for the Edit B2B picker (mirrors Create form's SearchableSelect)
  const { data: merchantsList = [] } = useQuery({
    queryKey: ['merchants-for-edit'],
    queryFn: async () => (await api.get('/merchants', { params: { pageSize: 500 } })).data as any,
    staleTime: 5 * 60_000,
    select: (raw: any) => Array.isArray(raw?.items) ? raw.items : Array.isArray(raw) ? raw : [],
  });
  const { data: couriers = [] } = useQuery({
    queryKey: ['couriers', order.orderType + '-active-edit'],
    queryFn: async () => (await api.get('/couriers', { params: { orderType: order.orderType, isActive: 'true' } })).data as any[],
  });

  const updateProduct = (idx: number, patch: any) => setProducts(arr => arr.map((p, i) => i === idx ? { ...p, ...patch } : p));
  const addProduct = () => setProducts(arr => [...arr, { name: '', sku: '', qty: 1, value: 0, hsn: '' }]);
  const removeProduct = (idx: number) => setProducts(arr => arr.length > 1 ? arr.filter((_, i) => i !== idx) : arr);

  const updateSub = (idx: number, patch: any) => setSubOrders(arr => arr.map((s, i) => i === idx ? { ...s, ...patch } : s));
  const addSub = () => setSubOrders(arr => [...arr, { qty: 1, length: 0, breadth: 0, height: 0, weight: 0 }]);
  const removeSub = (idx: number) => setSubOrders(arr => arr.filter((_, i) => i !== idx));

  // B2B: keep totalBoxes locked to sum(subOrders.qty) so the header count + table never drift.
  useEffect(() => {
    if (!isB2B) return;
    const sum = subOrders.reduce((acc: number, s: any) => acc + (Number(s?.qty) || 0), 0);
    if (sum > 0 && sum !== totalBoxes) setBoxes(sum);
  }, [isB2B, subOrders]);

  const save = useMutation({
    mutationFn: async () => {
      const body: any = {
        // Order ref
        orderNo,
        // Customer
        customerName: name, customerMobile: mobile, customerAltMobile: altMobile || null, customerEmail: email || null,
        customerAddress: addr, customerLandmark: landmark || null, customerPincode: pincode, customerCity: city, customerState: state,
        // Pickup
        warehouseId: warehouseId || null,
        pickupAddress, pickupPincode,
        pickupPersonName: pickupPersonName || null, pickupPersonPhone: pickupPersonPhone || null,
        // Courier
        courierId: courierId || null,
        // Payment
        deliveryType, codValue: deliveryType === 'COD' ? Number(codValue || 0) : 0,
        invoiceValue: Number(invoiceValue || 0),
        ewayBillNo: ewayBillNo || null,
        // Package
        chargeableWeight: chargeableWeight ? Number(chargeableWeight) : null,
        dimensions: (lengthCm || widthCm || heightCm) ? { length: lengthCm, breadth: widthCm, height: heightCm } : null,
        totalBoxes: Number(totalBoxes || 1),
        // Products
        products: products.map(p => ({ ...p, qty: Number(p.qty || 1), value: Number(p.value || 0) })),
        // Remarks
        userRemarks: userRemarks || null, // overridden below for B2B if invoiceDetails are present
        ...(merchantId !== '' ? { merchantId: Number(merchantId) } : {}),
        // Why this shipment was changed after it left. Mandatory post-ship; stored on the edit log
        // beside the field diff, because "what changed" without "why" settles no dispute.
        ...(postShip ? { editReason } : {}),
      };
      if (isB2B) {
        body.insuranceType = insuranceType || null;
        body.freightCollection = freightCollection || null;
        body.pickupType = pickupType || null;
        body.lrGenerateType = lrGenerateType;
        body.lrNo = lrGenerateType === '2' ? (lrNo || null) : null;
        body.subOrders = subOrders.map((s: any) => ({
          description: s.description ?? '',
          clientRefId: s.clientRefId ?? '',
          qty: Number(s.qty ?? 1),
          length: Number(s.length ?? 0),
          breadth: Number(s.breadth ?? 0),
          height: Number(s.height ?? 0),
          weight: Number(s.weight ?? 0),
        }));
        // B2B totalBoxes derives from sum of sub-orders qty (same as Create)
        const totalBoxesSum = body.subOrders.reduce((sum: number, s: any) => sum + (Number(s.qty) || 0), 0);
        if (totalBoxesSum > 0) body.totalBoxes = totalBoxesSum;
        // Mirror Create form's single-row product
        body.products = [{
          name: b2bProductName,
          project: b2bProjectName,
          qty: totalBoxesSum || Number(totalBoxes || 1),
          weight: Number(b2bTotalWeight || 0),
          isFragile: b2bIsFragile === 'Yes',
        }];
        if (b2bTotalWeight) body.chargeableWeight = Number(b2bTotalWeight);
        // For B2B the order-level dimensions[] = per-box L/B/H rows from subOrders
        body.dimensions = subOrders
          .filter((s: any) => Number(s.length) > 0 || Number(s.breadth) > 0 || Number(s.height) > 0)
          .map((s: any) => ({ qty: Number(s.qty ?? 1), length: Number(s.length ?? 0), breadth: Number(s.breadth ?? 0), height: Number(s.height ?? 0), weight: Number(s.weight ?? 0) }));
        // Invoice details — serialize into userRemarks JSON (same shape Create writes).
        // Compute order-level invoiceValue from sum of per-invoice rows to match Create.
        const cleanInvoice = invoiceDetails
          .map((iv: any) => ({
            productInvoiceValue: iv.productInvoiceValue === '' || iv.productInvoiceValue == null ? null : Number(iv.productInvoiceValue),
            invoiceNumber: iv.invoiceNumber || null,
            ewayBillNumber: iv.ewayBillNumber || null,
          }))
          .filter((iv: any) => iv.productInvoiceValue || iv.invoiceNumber || iv.ewayBillNumber);
        if (cleanInvoice.length > 0) {
          body.userRemarks = JSON.stringify({ invoiceDetails: cleanInvoice, freeText: userRemarks || '' });
          const invSum = cleanInvoice.reduce((s: number, iv: any) => s + (Number(iv.productInvoiceValue) || 0), 0);
          if (invSum > 0) body.invoiceValue = invSum;
        }
      }
      return (await api.patch(`/orders/${order.id}`, body)).data;
    },
    onSuccess: () => {
      // Edit may move a Booking-Failed (21) order to Not-Shipped (24) → refresh tab counts too
      qc.invalidateQueries({ predicate: (q: any) => Array.isArray(q.queryKey) && (q.queryKey[0] === 'orders' || q.queryKey[0] === 'order' || q.queryKey[0] === 'order-tab-counts') });
      onSaved();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Update failed'),
  });

  // Validation with named missing fields so user sees why Save is disabled
  const _name = (name || '').trim();
  const _addr = (addr || '').trim();
  const _city = (city || '').trim();
  const _state = (state || '').trim();
  const _mob = (mobile || '').trim();
  const _pin = (pincode || '').trim();
  const missing: string[] = [];
  if (!_name) missing.push('Customer Name');
  if (_mob.length !== 10) missing.push('Customer Mobile (10 digits)');
  if (!_addr) missing.push('Customer Address');
  if (_pin.length !== 6) missing.push('Customer Pincode (6 digits)');
  if (!_city) missing.push('City');
  if (!_state) missing.push('State');
  if (postShip && editReason.trim().length < 5) missing.push('Reason for this edit (min 5 characters)');
  const formValid = missing.length === 0;

  return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="font-semibold flex items-center gap-2"><Pencil size={14} /> Edit Order — {order.orderNo} <span className="text-xs text-slate-400 font-normal">({order.orderType})</span></h3>
          <button onClick={onClose}><X size={16} /></button>
        </div>
        <div className="p-5 space-y-5 overflow-y-auto flex-1">
          {postShip && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2">
              <div className="text-sm text-amber-900 font-medium flex items-center gap-1.5">
                <Truck size={14} /> This shipment has already been booked on the Gaint network
              </div>
              <p className="text-xs text-amber-800">
                Customer, pickup and package details can still be corrected — the delivery is ours, so the change reaches
                the field team. The merchant, courier, order number and waybill are locked; changing those means cancelling
                the waybill first. This edit is recorded against your name in the order's edit history.
              </p>
              <div>
                <div className="text-[11px] text-amber-900 mb-1 font-medium">Reason for this edit *</div>
                <input
                  value={editReason}
                  onChange={(e) => setEditReason(e.target.value)}
                  placeholder="e.g. Customer called — wrong flat number, corrected to B-402"
                  className="input w-full bg-white"
                  maxLength={500}
                  autoFocus
                />
              </div>
            </div>
          )}
          {isB2B ? (
          <>
          {/* ===== B2B Edit — mirrors the Create Order B2B form section-for-section ===== */}

          {/* 1. Order Information */}
          <FormSection title="Order Information">
            <div className="grid grid-cols-3 gap-3">
              <EditField label="Order No (Merchant Ref)" locked={lockRepoint}><input value={orderNo} onChange={(e) => setOrderNo(e.target.value)} disabled={lockRepoint} className={lockRepoint ? 'input bg-slate-50 text-slate-500' : 'input'} /></EditField>
              <EditField label="System Order No"><input value={order.systemOrderNo ?? ''} disabled className="input bg-slate-50 text-slate-500" /></EditField>
              {/* What the courier filed this shipment under — quote THIS on their portal, not the merchant's order no. */}
              <EditField label="Courier Order Ref"><input value={order.courierOrderRef ?? '—'} disabled className="input bg-slate-50 text-slate-500" /></EditField>
              <EditField label="LR Generate Type *" locked={lockRepoint}>
                <select value={lrGenerateType} onChange={(e) => setLrGenType(e.target.value as '1' | '2')} disabled={lockRepoint} className={lockRepoint ? 'input bg-slate-50 text-slate-500' : 'input'}>
                  <option value="1">Automatic</option>
                  <option value="2">Manual</option>
                </select>
              </EditField>
              {lrGenerateType === '2' && (
                <EditField label="LR No *" locked={lockRepoint}><input value={lrNo} onChange={(e) => setLrNo(e.target.value)} disabled={lockRepoint} className={lockRepoint ? 'input font-mono bg-slate-50 text-slate-500' : 'input font-mono'} /></EditField>
              )}
              <EditField label="Freight Collection Type *">
                <select value={freightCollection} onChange={(e) => setFc(e.target.value)} className="input">
                  <option value="">Select Type</option>
                  <option value="BILL_TO_CLIENT">Bill to Client</option>
                  <option value="COD">Collect on Delivery (COD)</option>
                </select>
              </EditField>
              <EditField label="Pickup Type *">
                <select value={pickupType} onChange={(e) => setPt(e.target.value)} className="input">
                  <option value="">Select Pickup Type</option>
                  <option value="FM_PICKUP">FM-Pickup</option>
                  <option value="SELF_DROP">Self-Drop</option>
                </select>
              </EditField>
              <div>
                <div className="text-[11px] text-slate-500 mb-1">Insurance Type</div>
                <div className="flex items-center gap-4 h-[38px]">
                  <label className="flex items-center gap-1 text-sm"><input type="radio" name="insuranceType" value="OWNER_RISK" checked={insuranceType === 'OWNER_RISK'} onChange={(e) => setIns(e.target.value)} /> OWNER RISK</label>
                  <label className="flex items-center gap-1 text-sm"><input type="radio" name="insuranceType" value="CARRIER_RISK" checked={insuranceType === 'CARRIER_RISK'} onChange={(e) => setIns(e.target.value)} /> CARRIER RISK</label>
                </div>
              </div>
              {isStaffUser && (
                <EditField label="Select Merchant *" locked={lockMerchant}>
                  <select value={merchantId === '' ? '' : String(merchantId)} onChange={(e) => setMerchantId(e.target.value ? Number(e.target.value) : '')} disabled={lockMerchant} className={lockMerchant ? 'input bg-slate-50 text-slate-500' : 'input'}>
                    <option value="">— Select Merchant —</option>
                    {(merchantsList as any[]).map((m: any) => (
                      <option key={m.id} value={String(m.id)}>#{m.id} · {m.companyName}</option>
                    ))}
                  </select>
                </EditField>
              )}
              <EditField label="Select Courier" locked={lockRepoint}>
                <select value={courierId} onChange={(e) => setCourierId(e.target.value ? Number(e.target.value) : '')} disabled={lockRepoint} className={lockRepoint ? 'input bg-slate-50 text-slate-500' : 'input'}>
                  <option value="">— Select —</option>
                  {couriers.map((c: any) => <option key={c.id} value={c.id}>#{c.id} · {c.name}</option>)}
                </select>
              </EditField>
              <EditField label="Pickup Location">
                <select value={warehouseId === '' ? '' : String(warehouseId)} onChange={(e) => onWarehouseChange(e.target.value ? Number(e.target.value) : '')} className="input">
                  <option value="">— Select —</option>
                  {warehouses.map((w: any) => <option key={w.id} value={String(w.id)}>{w.name} ({w.id})</option>)}
                </select>
              </EditField>
            </div>
            {warehouseId && (
              <div className="text-[11px] text-slate-500 px-1 mt-2">Pincode, person name and phone are auto-filled from the selected warehouse on save.</div>
            )}
          </FormSection>

          {/* 2. Shipping Information */}
          <FormSection title="Shipping Information">
            <div className="grid grid-cols-3 gap-3">
              <EditField label="Customer Mobile *"><input maxLength={10} inputMode="numeric" value={mobile} onChange={(e) => setMobile(String(e.target.value).replace(/\D/g, "").slice(-10))} className="input font-mono" /></EditField>
              <EditField label="Customer Name *"><input value={name} onChange={(e) => setName(e.target.value)} className="input" /></EditField>
              <EditField label="Customer Address *"><input value={addr} onChange={(e) => setAddr(e.target.value)} className="input" /></EditField>
              <EditField label="Customer Pincode *"><input maxLength={6} inputMode="numeric" value={pincode} onChange={(e) => setPincode(String(e.target.value).replace(/\D/g, "").slice(0, 6))} className="input font-mono" /></EditField>
              <EditField label="Customer City *"><input value={city} onChange={(e) => setCity(e.target.value)} className="input" /></EditField>
              <EditField label="Service State *"><select value={state} onChange={(e) => setState(e.target.value)} className="input"><option value="">— Select state —</option>{statesList.map((s: any) => <option key={s.id} value={s.name}>{s.name}</option>)}</select></EditField>
              {/* kept (edit-only) extras */}
              <EditField label="Alt Mobile"><input value={altMobile} onChange={(e) => setAlt(e.target.value.replace(/\D/g,'').slice(0,10))} className="input font-mono" /></EditField>
              <EditField label="Email"><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input" /></EditField>
              <EditField label="Landmark"><input value={landmark} onChange={(e) => setLandmark(e.target.value)} className="input" /></EditField>
            </div>
          </FormSection>

          {/* 3. Product Information */}
          <FormSection title="Product Information">
            <div className="grid grid-cols-4 gap-3">
              <EditField label="Product Name"><input value={b2bProductName} onChange={(e) => setB2bProductName(e.target.value)} className="input" /></EditField>
              <EditField label="Project Name"><input value={b2bProjectName} onChange={(e) => setB2bProjectName(e.target.value)} className="input" /></EditField>
              <EditField label="Total Weight In Kg(s)"><input type="number" step="0.001" value={b2bTotalWeight} onChange={(e) => setB2bTotalWeight(Number(e.target.value))} className="input" /></EditField>
              <EditField label="Is Fragile">
                <select value={b2bIsFragile} onChange={(e) => setB2bIsFragile(e.target.value as any)} className="input">
                  <option value="">Select</option>
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </select>
              </EditField>
            </div>
          </FormSection>

          {/* 4. Sub Orders (Description, Client reference ID / Order ID, Quantity) */}
          <FormSection title="Sub Orders" right={<button onClick={addSub} className="text-xs px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded">+ Add More</button>}>
            {subOrders.length === 0 && <div className="text-xs text-slate-400">No sub-orders. Click "+ Add More" to add one.</div>}
            {subOrders.length > 0 && (
              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-2 text-[10px] uppercase text-slate-500">
                  <div className="col-span-5">Description</div>
                  <div className="col-span-4">Client reference ID / Order ID</div>
                  <div className="col-span-2">Quantity</div>
                  <div className="col-span-1"></div>
                </div>
                {subOrders.map((s, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2">
                    <input value={s.description ?? ''} onChange={(e) => updateSub(i, { description: e.target.value })} className="input col-span-5" placeholder="Description" />
                    <input value={s.clientRefId ?? ''} onChange={(e) => updateSub(i, { clientRefId: e.target.value })} className="input col-span-4" placeholder="Client Ref ID" />
                    <input type="number" min="1" value={s.qty ?? 1} onChange={(e) => updateSub(i, { qty: Number(e.target.value) })} className="input col-span-2" />
                    <div className="col-span-1 flex items-center justify-center">
                      {subOrders.length > 1 && <button type="button" onClick={() => removeSub(i)} className="text-rose-600 text-xs hover:underline" title="Remove row">×</button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </FormSection>

          {/* 5. Dimensions Details in CM[s] — one row per Sub Order above */}
          <FormSection title="Dimensions Details in CM[s]" right={<button onClick={addSub} className="text-xs px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded">+ Add More</button>}>
            {subOrders.length === 0 && <div className="text-xs text-slate-400">Add a Sub Order row above to enter dimensions.</div>}
            {subOrders.length > 0 && (
              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-2 text-[10px] uppercase text-slate-500">
                  <div className="col-span-2">Qty</div>
                  <div className="col-span-3">Length (cm)</div>
                  <div className="col-span-3">Breadth (cm)</div>
                  <div className="col-span-2">Height (cm)</div>
                  <div className="col-span-2">Weight (kg)</div>
                </div>
                {subOrders.map((s, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2">
                    <input type="number" min="1" value={s.qty ?? 1} onChange={(e) => updateSub(i, { qty: Number(e.target.value) })} className="input col-span-2" />
                    <input type="number" min="0.1" step="0.1" value={s.length ?? 0} onChange={(e) => updateSub(i, { length: Number(e.target.value) })} className="input col-span-3" />
                    <input type="number" min="0.1" step="0.1" value={s.breadth ?? 0} onChange={(e) => updateSub(i, { breadth: Number(e.target.value) })} className="input col-span-3" />
                    <input type="number" min="0.1" step="0.1" value={s.height ?? 0} onChange={(e) => updateSub(i, { height: Number(e.target.value) })} className="input col-span-2" />
                    <input type="number" step="0.01" value={s.weight ?? 0} onChange={(e) => updateSub(i, { weight: Number(e.target.value) })} className="input col-span-2" />
                  </div>
                ))}
              </div>
            )}
          </FormSection>

          {/* 6. Invoice Details — Delivery Type + COD, then per-invoice rows */}
          <FormSection title="Invoice Details" right={<button onClick={addInvoice} className="text-xs px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded">+ Add More</button>}>
            <div className="grid grid-cols-4 gap-3 mb-3">
              <EditField label="Delivery Type *"><select value={deliveryType} onChange={(e) => setDeliveryType(e.target.value as any)} className="input"><option value="PREPAID">Prepaid</option><option value="COD">COD</option></select></EditField>
              {deliveryType === 'COD' && <EditField label="COD Amount *"><input type="number" value={codValue} onChange={(e) => setCod(Number(e.target.value))} className="input" /></EditField>}
              <EditField label="Invoice Value"><input type="number" value={invoiceValue} onChange={(e) => setInv(Number(e.target.value))} className="input" /></EditField>
              <EditField label="E-Way Bill No"><input value={ewayBillNo} onChange={(e) => setEway(e.target.value)} className="input font-mono" /></EditField>
            </div>
            {invoiceDetails.length > 0 && (
              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-2 text-[10px] uppercase text-slate-500">
                  <div className="col-span-3">Product Invoice Value</div>
                  <div className="col-span-4">Invoice Number</div>
                  <div className="col-span-4">E-way Bill Number</div>
                  <div className="col-span-1"></div>
                </div>
                {invoiceDetails.map((iv, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2">
                    <input type="number" step="0.01" min={0} inputMode="decimal" onKeyDown={(e) => { if (['e','E','+','-'].includes(e.key)) e.preventDefault(); }} value={iv.productInvoiceValue ?? ''} onChange={(e) => updateInvoice(i, { productInvoiceValue: e.target.value === '' ? '' : Number(e.target.value) })} className="input col-span-3" placeholder="0.00" />
                    <input value={iv.invoiceNumber ?? ''} onChange={(e) => updateInvoice(i, { invoiceNumber: e.target.value })} className="input col-span-4" placeholder="Invoice No" />
                    <input value={iv.ewayBillNumber ?? ''} onChange={(e) => updateInvoice(i, { ewayBillNumber: e.target.value })} className="input col-span-4 font-mono" placeholder="E-way Bill No" />
                    <div className="col-span-1 flex items-center justify-center">
                      {invoiceDetails.length > 1 && (<button type="button" onClick={() => removeInvoice(i)} className="text-rose-600 text-xs hover:underline">× Remove</button>)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </FormSection>

          {/* Remarks (kept) */}
          <FormSection title="Remarks">
            <textarea value={userRemarks} onChange={(e) => setUR(e.target.value)} className="input w-full min-h-[60px]" placeholder="Optional internal remarks…" />
          </FormSection>
          </>
          ) : (
          <>
          {/* Order Reference + Courier */}
          <FormSection title="Order Reference">
            <div className="grid grid-cols-4 gap-3">
              <EditField label="Order No (Merchant Ref)" locked={lockRepoint}><input value={orderNo} onChange={(e) => setOrderNo(e.target.value)} disabled={lockRepoint} className={lockRepoint ? 'input bg-slate-50 text-slate-500' : 'input'} /></EditField>
              {isStaffUser && (
                <EditField label="Merchant" locked={lockMerchant}>
                  <select value={merchantId === '' ? '' : String(merchantId)} onChange={(e) => setMerchantId(e.target.value ? Number(e.target.value) : '')} disabled={lockMerchant} className={lockMerchant ? 'input bg-slate-50 text-slate-500' : 'input'}>
                    <option value="">— Select Merchant —</option>
                    {(merchantsList as any[]).map((m: any) => (
                      <option key={m.id} value={String(m.id)}>#{m.id} · {m.companyName}</option>
                    ))}
                  </select>
                </EditField>
              )}
              <EditField label="Courier" locked={lockRepoint}>
                <select value={courierId} onChange={(e) => setCourierId(e.target.value ? Number(e.target.value) : '')} disabled={lockRepoint} className={lockRepoint ? 'input bg-slate-50 text-slate-500' : 'input'}>
                  <option value="">— Select —</option>
                  {couriers.map((c: any) => <option key={c.id} value={c.id}>#{c.id} · {c.name}</option>)}
                </select>
              </EditField>
              <EditField label="System Order No"><input value={order.systemOrderNo ?? ''} disabled className="input bg-slate-50 text-slate-500" /></EditField>
              {/* What the courier filed this shipment under — quote THIS on their portal, not the merchant's order no. */}
              <EditField label="Courier Order Ref"><input value={order.courierOrderRef ?? '—'} disabled className="input bg-slate-50 text-slate-500" /></EditField>
            </div>
          </FormSection>

          {/* Customer */}
          <FormSection title="Customer / Consignee">
            <div className="grid grid-cols-3 gap-3">
              <EditField label="Name *"><input value={name} onChange={(e) => setName(e.target.value)} className="input" /></EditField>
              <EditField label="Mobile *"><input maxLength={10} inputMode="numeric" value={mobile} onChange={(e) => setMobile(String(e.target.value).replace(/\D/g, "").slice(-10))} className="input font-mono" /></EditField>
              <EditField label="Alt Mobile"><input value={altMobile} onChange={(e) => setAlt(e.target.value.replace(/\D/g,'').slice(0,10))} className="input font-mono" /></EditField>
              <EditField label="Email"><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input" /></EditField>
              <div className="col-span-2"><EditField label="Address *"><input value={addr} onChange={(e) => setAddr(e.target.value)} className="input" /></EditField></div>
              <EditField label="Landmark"><input value={landmark} onChange={(e) => setLandmark(e.target.value)} className="input" /></EditField>
              <EditField label="Pincode *"><input maxLength={6} inputMode="numeric" value={pincode} onChange={(e) => setPincode(String(e.target.value).replace(/\D/g, "").slice(0, 6))} className="input font-mono" /></EditField>
              <EditField label="City *"><input value={city} onChange={(e) => setCity(e.target.value)} className="input" /></EditField>
              <EditField label="State *"><select value={state} onChange={(e) => setState(e.target.value)} className="input"><option value="">— Select state —</option>{statesList.map((s: any) => <option key={s.id} value={s.name}>{s.name}</option>)}</select></EditField>
            </div>
          </FormSection>

          {/* Pickup */}
          <FormSection title="Pickup Location">
            {isB2B ? (
              <div className="grid grid-cols-1 gap-3">
                <EditField label="Pickup Location">
                  <select value={warehouseId === '' ? '' : String(warehouseId)}
                    onChange={(e) => onWarehouseChange(e.target.value ? Number(e.target.value) : '')} className="input">
                    <option value="">— Select —</option>
                    {warehouses.map((w: any) => <option key={w.id} value={String(w.id)}>{w.name} ({w.id})</option>)}
                  </select>
                </EditField>
                {warehouseId && (
                  <div className="text-[11px] text-slate-500 px-1">Pincode, person name and phone are auto-filled from the selected warehouse on save.</div>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-3">
                <EditField label="Pickup Location">
                  <select value={warehouseId === '' ? '' : String(warehouseId)}
                    onChange={(e) => onWarehouseChange(e.target.value ? Number(e.target.value) : '')} className="input">
                    <option value="">— Select —</option>
                    {warehouses.map((w: any) => <option key={w.id} value={String(w.id)}>#{w.id} · {w.name}{w.code ? ` (${w.code})` : ''}</option>)}
                  </select>
                </EditField>
                <EditField label="Pickup Pincode"><input maxLength={6} inputMode="numeric" value={pickupPincode} onChange={(e) => setPp(String(e.target.value).replace(/\D/g, "").slice(0, 6))} className="input font-mono" /></EditField>
                <EditField label="Person Name"><input value={pickupPersonName} onChange={(e) => setPpn(e.target.value)} className="input" /></EditField>
                <EditField label="Person Phone"><input maxLength={10} inputMode="numeric" value={pickupPersonPhone} onChange={(e) => setPph(String(e.target.value).replace(/\D/g, "").slice(-10))} className="input font-mono" /></EditField>
                <div className="col-span-3"><EditField label="Pickup Address"><input value={pickupAddress} onChange={(e) => setPa(e.target.value)} className="input" /></EditField></div>
              </div>
            )}
          </FormSection>

          {/* Payment & Invoice */}
          <FormSection title="Payment & Invoice">
            <div className="grid grid-cols-4 gap-3">
              <EditField label="Delivery Type"><select value={deliveryType} onChange={(e) => setDeliveryType(e.target.value as any)} className="input"><option value="PREPAID">PREPAID</option><option value="COD">COD</option></select></EditField>
              {deliveryType === 'COD' && <EditField label="COD Amount *"><input type="number" value={codValue} onChange={(e) => setCod(Number(e.target.value))} className="input" /></EditField>}
              <EditField label="Invoice Value"><input type="number" value={invoiceValue} onChange={(e) => setInv(Number(e.target.value))} className="input" /></EditField>
              <EditField label="E-Way Bill No"><input value={ewayBillNo} onChange={(e) => setEway(e.target.value)} className="input font-mono" /></EditField>
            </div>
          </FormSection>

          {/* Package / Dimensions — B2C only. For B2B, dimensions come from per-box Sub-Orders rows below. */}
          {!isB2B && (
            <FormSection title="Package / Dimensions">
              <div className="grid grid-cols-5 gap-3">
                <EditField label="Weight (kg)"><input type="number" step="0.001" value={chargeableWeight as any} onChange={(e) => setWt(e.target.value as any)} className="input" /></EditField>
                <EditField label="Length (cm)"><input type="number" value={lengthCm} onChange={(e) => setLen(Number(e.target.value))} className="input" /></EditField>
                <EditField label="Breadth (cm)"><input type="number" value={widthCm} onChange={(e) => setBr(Number(e.target.value))} className="input" /></EditField>
                <EditField label="Height (cm)"><input type="number" value={heightCm} onChange={(e) => setHei(Number(e.target.value))} className="input" /></EditField>
                <EditField label="Total Boxes"><input type="number" value={totalBoxes} onChange={(e) => setBoxes(Number(e.target.value))} className="input" /></EditField>
              </div>
            </FormSection>
          )}

          {/* Products (B2C only — B2B uses the single-row block below) */}
          {!isB2B && <FormSection title="Products" right={<button onClick={addProduct} className="text-xs px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded">+ Add Product</button>}>
            <div className="space-y-2">
              <div className="grid grid-cols-12 gap-2 text-[10px] uppercase text-slate-500">
                <div className="col-span-4">Name</div>
                <div className="col-span-2">SKU</div>
                <div className="col-span-1">Qty</div>
                <div className="col-span-2">Value (₹)</div>
                <div className="col-span-2">HSN</div>
                <div className="col-span-1"></div>
              </div>
              {products.map((p, i) => (
                <div key={i} className="grid grid-cols-12 gap-2">
                  <input value={p.name ?? ''} onChange={(e) => updateProduct(i, { name: e.target.value })} className="input col-span-4" placeholder="Product name" />
                  <input value={p.sku ?? ''} onChange={(e) => updateProduct(i, { sku: e.target.value })} className="input col-span-2" />
                  <input type="number" value={p.qty ?? 1} onChange={(e) => updateProduct(i, { qty: Number(e.target.value) })} className="input col-span-1" />
                  <input type="number" value={p.value ?? 0} onChange={(e) => updateProduct(i, { value: Number(e.target.value) })} className="input col-span-2" />
                  <input value={p.hsn ?? ''} onChange={(e) => updateProduct(i, { hsn: e.target.value })} className="input col-span-2" />
                  <button onClick={() => removeProduct(i)} className="col-span-1 text-rose-600 hover:bg-rose-50 rounded text-xs" disabled={products.length <= 1}>Remove</button>
                </div>
              ))}
            </div>
          </FormSection>}

          {/* B2B specifics */}
          {isB2B && (
            <>
              <FormSection title="B2B Logistics Settings">
                <div className="grid grid-cols-4 gap-3">
                  <EditField label="Booking Type">
                    <select value={lrGenerateType} onChange={(e) => setLrGenType(e.target.value as '1' | '2')} className="input">
                      <option value="1">Automatic</option>
                      <option value="2">Manual</option>
                    </select>
                  </EditField>
                  {lrGenerateType === '2' && (
                    <EditField label="LR No"><input value={lrNo} onChange={(e) => setLrNo(e.target.value)} className="input font-mono" /></EditField>
                  )}
                  <EditField label="Freight Collection Type">
                    <select value={freightCollection} onChange={(e) => setFc(e.target.value)} className="input">
                      <option value="">Select Type</option>
                      <option value="BILL_TO_CLIENT">Bill to Client</option>
                      <option value="COD">Collect on Delivery (COD)</option>
                    </select>
                  </EditField>
                  <EditField label="Pickup Type">
                    <select value={pickupType} onChange={(e) => setPt(e.target.value)} className="input">
                      <option value="">Select Pickup Type</option>
                      <option value="FM_PICKUP">FM-Pickup</option>
                      <option value="SELF_DROP">Self-Drop</option>
                    </select>
                  </EditField>
                  <EditField label="Insurance Type">
                    <select value={insuranceType} onChange={(e) => setIns(e.target.value)} className="input">
                      <option value="OWNER_RISK">Owner Risk</option>
                      <option value="CARRIER_RISK">Carrier Risk</option>
                    </select>
                  </EditField>
                </div>
              </FormSection>
              <FormSection title="Product Information">
                <div className="grid grid-cols-4 gap-3">
                  <EditField label="Product Name"><input value={b2bProductName} onChange={(e) => setB2bProductName(e.target.value)} className="input" /></EditField>
                  <EditField label="Project Name"><input value={b2bProjectName} onChange={(e) => setB2bProjectName(e.target.value)} className="input" /></EditField>
                  <EditField label="Total Weight In Kg(s)"><input type="number" step="0.001" value={b2bTotalWeight} onChange={(e) => setB2bTotalWeight(Number(e.target.value))} className="input" /></EditField>
                  <EditField label="Is Fragile">
                    <select value={b2bIsFragile} onChange={(e) => setB2bIsFragile(e.target.value as any)} className="input">
                      <option value="">Select</option>
                      <option value="No">No</option>
                      <option value="Yes">Yes</option>
                    </select>
                  </EditField>
                </div>
              </FormSection>
              <FormSection title="Sub Orders" right={<button onClick={addSub} className="text-xs px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded">+ Add More</button>}>
                {subOrders.length === 0 && <div className="text-xs text-slate-400">No sub-orders. Click "+ Add More" to add one.</div>}
                {subOrders.length > 0 && (
                  <div className="space-y-2">
                    <div className="grid grid-cols-12 gap-2 text-[10px] uppercase text-slate-500">
                      <div className="col-span-4">Description</div>
                      <div className="col-span-3">Client Ref ID / Order ID</div>
                      <div className="col-span-1">Qty</div>
                      <div className="col-span-1">L (cm)</div>
                      <div className="col-span-1">B (cm)</div>
                      <div className="col-span-1">H (cm)</div>
                      <div className="col-span-1">Wt (kg)</div>
                    </div>
                    {subOrders.map((s, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2">
                        <input value={s.description ?? ''} onChange={(e) => updateSub(i, { description: e.target.value })} className="input col-span-4" placeholder="Description" />
                        <input value={s.clientRefId ?? ''} onChange={(e) => updateSub(i, { clientRefId: e.target.value })} className="input col-span-3" placeholder="Client Ref ID" />
                        <input type="number" min="1" value={s.qty ?? 1} onChange={(e) => updateSub(i, { qty: Number(e.target.value) })} className="input col-span-1" />
                        <input type="number" min="0.1" step="0.1" value={s.length ?? 0} onChange={(e) => updateSub(i, { length: Number(e.target.value) })} className="input col-span-1" />
                        <input type="number" min="0.1" step="0.1" value={s.breadth ?? 0} onChange={(e) => updateSub(i, { breadth: Number(e.target.value) })} className="input col-span-1" />
                        <input type="number" min="0.1" step="0.1" value={s.height ?? 0} onChange={(e) => updateSub(i, { height: Number(e.target.value) })} className="input col-span-1" />
                        <input type="number" step="0.01" value={s.weight ?? 0} onChange={(e) => updateSub(i, { weight: Number(e.target.value) })} className="input col-span-1" />
                      </div>
                    ))}
                    <div className="text-right pt-1">
                      {subOrders.length > 1 && <button onClick={() => removeSub(subOrders.length - 1)} className="text-rose-600 text-xs hover:underline">× Remove last row</button>}
                    </div>
                  </div>
                )}
              </FormSection>
              {/* Per-invoice rows — Product Invoice Value, Invoice No, E-Way Bill No.
                  Persisted into Order.userRemarks JSON.invoiceDetails so booking flows can read them. */}
              <FormSection title="Invoice Details" right={<button onClick={addInvoice} className="text-xs px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded">+ Add More</button>}>
                {invoiceDetails.length === 0 && <div className="text-xs text-slate-400">No invoice rows. Click "+ Add More".</div>}
                {invoiceDetails.length > 0 && (
                  <div className="space-y-2">
                    <div className="grid grid-cols-12 gap-2 text-[10px] uppercase text-slate-500">
                      <div className="col-span-3">Product Invoice Value</div>
                      <div className="col-span-4">Invoice Number</div>
                      <div className="col-span-4">E-way Bill Number</div>
                      <div className="col-span-1"></div>
                    </div>
                    {invoiceDetails.map((iv, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2">
                        <input
                          type="number" step="0.01" min={0} inputMode="decimal"
                          onKeyDown={(e) => { if (['e','E','+','-'].includes(e.key)) e.preventDefault(); }}
                          value={iv.productInvoiceValue ?? ''}
                          onChange={(e) => updateInvoice(i, { productInvoiceValue: e.target.value === '' ? '' : Number(e.target.value) })}
                          className="input col-span-3" placeholder="0.00" />
                        <input value={iv.invoiceNumber ?? ''} onChange={(e) => updateInvoice(i, { invoiceNumber: e.target.value })} className="input col-span-4" placeholder="Invoice No" />
                        <input value={iv.ewayBillNumber ?? ''} onChange={(e) => updateInvoice(i, { ewayBillNumber: e.target.value })} className="input col-span-4 font-mono" placeholder="E-way Bill No" />
                        <div className="col-span-1 flex items-center justify-center">
                          {invoiceDetails.length > 1 && (
                            <button type="button" onClick={() => removeInvoice(i)} className="text-rose-600 text-xs hover:underline">× Remove</button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </FormSection>
            </>
          )}

          {/* Remarks */}
          <FormSection title="Remarks">
            <textarea value={userRemarks} onChange={(e) => setUR(e.target.value)} className="input w-full min-h-[60px]" placeholder="Optional internal remarks…" />
          </FormSection>
          </>
          )}
        </div>
        <div className="flex items-center justify-between gap-2 border-t px-5 py-3 bg-white">
          <div className="text-xs text-rose-600 truncate">
            {!formValid && missing.length > 0 && <span><span className="font-semibold">Missing:</span> {missing.join(', ')}</span>}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-1.5 text-sm border rounded">Cancel</button>
            <button onClick={() => save.mutate()} disabled={!formValid || save.isPending}
              title={!formValid ? `Missing: ${missing.join(', ')}` : ''}
              className="px-4 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-40 flex items-center gap-1">
              <Save size={13} /> {save.isPending ? 'Saving…' : 'Save All Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FormSection({ title, children, right }: { title: string; children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/30">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold text-slate-600 uppercase">{title}</div>
        {right}
      </div>
      {children}
    </div>
  );
}

/**
 * `locked` marks a field that is disabled because the shipment has already been booked — it says so
 * on the label rather than leaving the user to wonder why a box they can see won't accept typing.
 * A required marker is dropped when locked; there is nothing to fill in.
 */
function EditField({ label, children, locked = false }: { label: string; children: React.ReactNode; locked?: boolean }) {
  const required = label.trim().endsWith('*');
  const base = required ? label.replace(/\s*\*\s*$/, '') : label;
  return (
    <div>
      <div className="text-[11px] text-slate-500 mb-1 flex items-center gap-1">
        {base}
        {required && !locked && <span className="text-rose-500 ml-0.5">*</span>}
        {locked && <span className="text-[10px] bg-slate-100 text-slate-500 px-1 rounded" title="Locked — this order is already booked. Cancel the waybill to change it.">locked</span>}
      </div>
      {children}
    </div>
  );
}

function Section({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="text-sm font-semibold text-slate-900 flex items-center gap-2 mb-3">{icon} {title}</div>
      {children}
    </div>
  );
}
function Grid({ children }: { children: React.ReactNode }) { return <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">{children}</div>; }
function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-slate-900">{value ?? '—'}</div>
    </div>
  );
}



function DownloadDropdown({ orderId, orderType }: { orderId: string; orderType: 'B2C' | 'B2B' }) {
  const [open, setOpen] = useState(false);
  async function go(kind: 'labels'|'invoices'|'waybills'|'delivery-challans', opts?: { size?: string; template?: string }) {
    setOpen(false);
    const url = `${API_BASE_URL}/orders/${kind}`;
    const token = tokenStore.get();
    const body: any = { orderIds: [orderId] };
    if (kind === 'labels') { body.size = opts?.size; body.template = opts?.template; }
    const r = await fetch(url, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    if (!r.ok) return;
    const html = await r.text();
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.open(); w.document.write(html); w.document.close();
  }

  const items = orderType === 'B2C'
    ? [
        { label: 'Print Label', icon: <Printer size={14} />, run: () => go('labels') },
        { label: 'Print Invoice', icon: <FileText size={14} />, run: () => go('invoices') },
      ]
    : [
        { label: 'B2B Labels — 3×2', icon: <Download size={14} />, run: () => go('labels', { size: '3x2', template: 'B2B_3X2' }) },
        { label: 'B2B Labels — 4×6', icon: <Download size={14} />, run: () => go('labels', { size: '4x6', template: 'B2B_4X6' }) },
        { label: 'B2B Labels — A4', icon: <Download size={14} />, run: () => go('labels', { template: 'B2B_A4' }) },
        { label: 'Waybill Copy (3 pages)', icon: <Download size={14} />, run: () => go('waybills') },
        { label: 'Invoice', icon: <FileText size={14} />, run: () => go('invoices') },
        { label: 'Delivery Challan', icon: <FileText size={14} />, run: () => go('delivery-challans') },
      ];

  return (
    <div className="relative">
      <button onClick={() => setOpen(o => !o)}
        className={`${ACTION_BTN} bg-primary text-white border-primary hover:bg-primary/90`}>
        <Download size={14} /> Downloads <ChevronDown size={14} />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-[1000] min-w-[220px]">
          {items.map(i => (
            <button key={i.label} onClick={i.run}
              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2">
              {i.icon} {i.label}
            </button>
          ))}
        </div>
      )}


    </div>
  );
}

// B2B boxes table with per-box scan history. Each box shows its last scan date/time and a History
// button that expands the full scan trail for that specific child AWB.
function BoxAwbTable({ rows }: { rows: NonNullable<OrderDetail['subAwbs']> }) {
  const [open, setOpen] = useState<number | null>(null);
  return (
    <table className="w-full text-sm">
      <thead className="text-xs uppercase text-slate-500">
        <tr>
          <th className="text-left py-1.5">Box</th>
          <th className="text-left py-1.5">Label</th>
          <th className="text-left py-1.5">AWB</th>
          <th className="text-left py-1.5">Weight</th>
          <th className="text-left py-1.5">Dimensions</th>
          <th className="text-left py-1.5">Last Scan</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(s => {
          const scans = s.scans ?? [];
          const count = s.scanCount ?? scans.length;
          return (
            <Fragment key={s.id}>
              <tr className="border-t border-slate-100">
                <td className="py-2">{s.isMaster ? '—' : s.label === 'Document' ? 'Doc' : `${s.boxNo}/${s.totalBoxes}`}</td>
                <td className="py-2"><span className={`text-xs px-2 py-0.5 rounded ${s.isMaster ? 'bg-blue-100 text-blue-700' : s.label === 'Document' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'}`}>{s.label ?? (s.isMaster ? 'Master' : 'Child')}</span></td>
                <td className="py-2 font-mono text-xs"><CopyText value={s.awbNo} display={<span>{s.awbNo}</span>} iconSize={10} silent /></td>
                <td className="py-2 text-xs">{s.weight ? `${s.weight} kg` : '—'}</td>
                <td className="py-2 text-xs">{s.dimensions ? `${s.dimensions.length} × ${s.dimensions.breadth} × ${s.dimensions.height} cm` : '—'}</td>
                <td className="py-2 text-xs">
                  {s.lastScanAt ? (
                    <div className="flex items-center gap-2">
                      <span className="text-slate-700">{new Date(s.lastScanAt).toLocaleString('en-IN')}</span>
                      {count > 0 && (
                        <button onClick={() => setOpen(open === s.id ? null : s.id)} className="text-primary text-[11px] underline whitespace-nowrap">
                          {open === s.id ? 'Hide' : `History (${count})`}
                        </button>
                      )}
                    </div>
                  ) : <span className="text-slate-400">Not scanned</span>}
                </td>
              </tr>
              {open === s.id && scans.length > 0 && (
                <tr className="bg-slate-50/70">
                  <td colSpan={6} className="px-3 py-2">
                    <div className="space-y-1.5">
                      {scans.map(ev => (
                        <div key={ev.id} className="flex flex-wrap items-center gap-2 text-xs">
                          <span className="text-slate-500 w-40 shrink-0">{new Date(ev.eventDateTime).toLocaleString('en-IN')}</span>
                          <span className={`px-1.5 py-0.5 rounded ${STATUS_TONE[ev.statusId] ?? 'bg-slate-100 text-slate-600'}`}>{STATUS_LABEL[ev.statusId] ?? `#${ev.statusId}`}</span>
                          {(ev.location || ev.scanCity || ev.scanState) && <span className="text-slate-500">{ev.location || [ev.scanCity, ev.scanState, ev.scanPincode].filter(Boolean).join(', ')}</span>}
                          {ev.statusMessage && <span className="text-slate-600">· {ev.statusMessage}</span>}
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

export function OrderDetailB2cPage() { return <OrderDetailPage orderType="B2C" />; }
export function OrderDetailB2bPage() { return <OrderDetailPage orderType="B2B" />; }

function BookingSourceBadge({ type, channelOrderId }: { type?: string; channelOrderId?: string }) {
  const isManual = !type || type === 'MANUAL';
  const cls = isManual ? 'bg-slate-100 text-slate-700 border-slate-200' : 'bg-violet-50 text-violet-700 border-violet-200';
  const label = isManual ? 'Manual' : (type ?? 'API');
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border ${cls}`}>
      {label}
      {channelOrderId && <span className="text-slate-400">·</span>}
      {channelOrderId && <span className="font-mono text-[10px]">{channelOrderId}</span>}
    </span>
  );
}
